import {reactive, ref, toRaw} from "vue";
import axios from "axios";
import {store} from "./boot/vuex";
import {BigNumber, ethers} from "ethers";
import {
  AUTHENTICATE_ENDPOINT,
  AUTH_ROLES,
  CHECK_AUTH,
  DEVELOPMENT_ENV,
  ACTIVE_CHAIN,

  OFFERING_STATES,
  PROJECT_ENDED,
  AGROFORESTRY_PROJECT_ENDPOINT,
  CARBON_PROJECT_ENDPOINT,
  PROJECT_TYPES,
  TOKEN_DECIMALS,
} from "./constants";
import TransportWebUSB from "@ledgerhq/hw-transport-webusb";
import Eth from "@ledgerhq/hw-app-eth";
import {Buffer} from "buffer";
import {detectWallets, getPreferredProviderEthers,} from "./shared";
import { ABI_REGISTRY } from "src/contracts/abiRegistry";
import {
  hasSignedIssuance,
  readLatestIssuance,
} from "src/utils/issuanceSummary";
import { getCarbonTimelineHorizon } from "src/utils/projectData";
import { isAssignedProjectAuditor } from "src/utils/auditorAssignments";
import { hasMeaningfulValue } from "src/utils/valueChecks";
import {
  requireMatchingWalletAddress,
  resolveInjectedWalletAuthority,
} from "src/utils/injectedWalletAuthority";
import {
  forgetRejectedAuthAttempt,
  hasRejectedAuthAttempt,
  rememberRejectedAuthAttempt,
} from "src/utils/authRetryGuard";
import {
  authRoleMatches,
  hasAuthenticatedRole,
  shouldDeferAuthenticationWatch,
} from "src/utils/authRolePolicy";
import { isLocalTestnetMetaMaskEnabled } from "src/utils/localTestnetAuth";
import { resolveWalletAuthErrorMessage } from "src/utils/authSessionErrors";
import {Notify} from "quasar";
import { MetaKeep } from "metakeep";
import { isCurrentV2CarbonProject } from "src/features/carbon/lifecycle/lifecycleOperations";
import { isAssignedV2ProjectCertifier } from "src/features/carbon/auditor/auditorProjectState";

if (typeof window !== "undefined") {
  window.Buffer = Buffer;
}

const HEADER_VALUE_FORBIDDEN_REGEX = /[\r\n]/;
const HEADER_NAME_SAFE_REGEX = /^[A-Za-z0-9-]+$/;
const HEADER_VALUE_MAX_LENGTH = 1024;
const objectToString = Object.prototype.toString;
const isPlainObject = (value) => objectToString.call(value) === "[object Object]";
const metaKeepInflight = new Map();
const authInflight = new Map();
let authCheckInflight = null;
let authSessionExpiryTimer = null;
let walletIdentityClearPromise = Promise.resolve();
let walletIdentityClearError = null;
const METAKEEP_BUSINESS_WALLET_KEY = "metakeep_business_wallet_address";
const METAKEEP_AUTH_WALLET_KEY = "metakeep_auth_wallet_address";
const LOGOUT_ENDPOINT = "logout";
const MAX_AUTH_EXPIRY_TIMER_DELAY_MS = 2_147_483_647;

const createMetaKeepSdk = ({ email, phone } = {}) => {
  const metakeepId = process.env.VUE_APP_METAKEEP_ID;
  if (!metakeepId) {
    return null;
  }
  const MetaKeepCtor =
    typeof window !== "undefined" &&
    window.__CARBON_V2_LOCAL_HARNESS__ === true &&
    window.MetaKeep
      ? window.MetaKeep
      : MetaKeep;
  const user = email ? { email } : phone ? { phone } : null;
  return new MetaKeepCtor({
    appId: metakeepId,
    chainId: ACTIVE_CHAIN.chainId,
    rpcNodeUrls: {
      [ACTIVE_CHAIN.chainId]: ACTIVE_CHAIN.rpcEndPoint,
    },
    ...(user ? { user } : {}),
  });
};

const isDevEnv = () => process.env.VUE_APP_ENV === DEVELOPMENT_ENV;

const redactValue = (value, keep = 4) => {
  if (value === null || value === undefined) {
    return value;
  }
  const text = String(value);
  if (!text) {
    return text;
  }
  if (text.length <= keep * 2) {
    return `${text.slice(0, 1)}…`;
  }
  return `${text.slice(0, keep)}…${text.slice(-keep)}`;
};

export const logDev = (tag, ...args) => {
  if (!isDevEnv()) {
    return;
  }
  const formatted = args.map((a) =>
    typeof a === "object" && a !== null ? JSON.stringify(a, null, 2) : a
  );
  console.log(`[${tag}]`, ...formatted);
};

const canUseStorage = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const readStorageValue = (key) => {
  if (!canUseStorage()) {
    return null;
  }
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorageValue = (key, value) => {
  if (!canUseStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(key, value);
  } catch {}
};

const removeStorageValue = (key) => {
  if (!canUseStorage()) {
    return;
  }
  try {
    window.localStorage.removeItem(key);
  } catch {}
};

export const getStoredMetaKeepSession = () => {
  return {
    businessWalletAddress: readStorageValue(METAKEEP_BUSINESS_WALLET_KEY),
    authWalletAddress: readStorageValue(METAKEEP_AUTH_WALLET_KEY),
  };
};

const persistMetaKeepSession = ({ businessWalletAddress, authWalletAddress }) => {
  if (businessWalletAddress) {
    writeStorageValue(METAKEEP_BUSINESS_WALLET_KEY, businessWalletAddress);
  }
  if (authWalletAddress) {
    writeStorageValue(METAKEEP_AUTH_WALLET_KEY, authWalletAddress);
  }
};

export const clearMetaKeepSession = () => {
  removeStorageValue(METAKEEP_BUSINESS_WALLET_KEY);
  removeStorageValue(METAKEEP_AUTH_WALLET_KEY);
};

const setCarbonSession = ({
  businessWalletAddress = null,
  authWalletAddress = null,
} = {}) => {
  store.commit("setCarbonSession", {
    businessWalletAddress,
    authWalletAddress,
  });
};

const loadLocalDirectWalletSessionIdentity = async ({
  expectedAuthWallet,
  expectedRole,
}) => {
  setCarbonSession();
  const response = await axios.get(
    getURLFromPieces("v2", "session-identity")
  );
  const identity = response?.data;
  const authWalletAddress = identity?.authWalletAddress;
  const businessWalletAddress = identity?.businessWalletAddress;
  if (
    !ethers.utils.isAddress(expectedAuthWallet || "") ||
    !Number.isInteger(expectedRole) ||
    !ethers.utils.isAddress(authWalletAddress || "") ||
    !ethers.utils.isAddress(businessWalletAddress || "") ||
    !Number.isInteger(identity?.role) ||
    identity.role !== expectedRole ||
    authWalletAddress.toLowerCase() !== expectedAuthWallet.toLowerCase() ||
    businessWalletAddress.toLowerCase() !== authWalletAddress.toLowerCase()
  ) {
    throw new Error("Backend direct-wallet identity mismatch.");
  }
  setCarbonSession({ businessWalletAddress, authWalletAddress });
  return { businessWalletAddress, authWalletAddress };
};

export const normalizeAuthSessionExpiry = (expiresAt) => {
  if (expiresAt === null || expiresAt === undefined || expiresAt === "") {
    return null;
  }
  if (expiresAt instanceof Date) {
    const value = expiresAt.getTime();
    return Number.isFinite(value) ? value : null;
  }
  if (typeof expiresAt === "number") {
    if (!Number.isFinite(expiresAt) || expiresAt < 0) {
      return null;
    }
    return expiresAt < 1e12 ? expiresAt * 1000 : expiresAt;
  }
  if (typeof expiresAt !== "string") {
    return null;
  }

  const trimmed = expiresAt.trim();
  if (!trimmed) {
    return null;
  }
  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    return normalizeAuthSessionExpiry(Number(trimmed));
  }
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/i.test(
      trimmed
    )
  ) {
    return null;
  }

  // The backend currently serializes its UTC datetime without a timezone
  // suffix. Browsers otherwise interpret that timestamp as local time.
  const hasExplicitTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const normalizedIso =
    /^\d{4}-\d{2}-\d{2}T/.test(trimmed) && !hasExplicitTimezone
      ? `${trimmed}Z`
      : trimmed;
  const parsed = Date.parse(normalizedIso);
  return Number.isFinite(parsed) ? parsed : null;
};

const clearAuthSessionExpiryTimer = () => {
  if (authSessionExpiryTimer !== null) {
    clearTimeout(authSessionExpiryTimer);
    authSessionExpiryTimer = null;
  }
};

export const invalidateAuthSession = () => {
  clearAuthSessionExpiryTimer();
  if (axios.defaults?.headers?.common) {
    delete axios.defaults.headers.common.Authorization;
  }
  store.commit("setAuthSessionExpiresAt", null);
  store.commit("setAdminRole", null);
  store.commit("setIsAuthenticated", false);
};

export const clearAuthenticatedWalletSession = () => {
  invalidateAuthSession();
  setCarbonSession();
  store.commit("setBalances", {});
  store.commit("setUSDCBalance", 0);
  store.commit("setBctBalance", 0);
  walletIdentityClearPromise = walletIdentityClearPromise
    .catch(() => {})
    .then(async () => {
      walletIdentityClearError = null;
      try {
        await axios.post(getURLFromPieces(LOGOUT_ENDPOINT), null, {
          withCredentials: true,
        });
      } catch (error) {
        walletIdentityClearError = error;
        logDev("auth", "wallet account-change logout failed", {
          status: error?.response?.status ?? null,
          message: error?.message || String(error),
        });
      }
    });
  return walletIdentityClearPromise;
};

export const waitForAuthenticatedWalletSessionClear = async () => {
  await walletIdentityClearPromise;
  if (walletIdentityClearError) {
    throw new Error(
      "The previous wallet session could not be cleared. Reconnect and try again."
    );
  }
};

const armAuthSessionExpiryTimer = (expiresAt) => {
  const remainingMs = expiresAt - Date.now();
  if (remainingMs <= 0) {
    invalidateAuthSession();
    return;
  }
  authSessionExpiryTimer = setTimeout(() => {
    authSessionExpiryTimer = null;
    armAuthSessionExpiryTimer(expiresAt);
  }, Math.min(remainingMs, MAX_AUTH_EXPIRY_TIMER_DELAY_MS));
};

const applyAuthSessionExpiry = (expiresAt) => {
  clearAuthSessionExpiryTimer();
  const normalizedExpiry = normalizeAuthSessionExpiry(expiresAt);
  store.commit("setAuthSessionExpiresAt", normalizedExpiry);
  if (normalizedExpiry !== null) {
    armAuthSessionExpiryTimer(normalizedExpiry);
  }
  return normalizedExpiry;
};

export const isAuthSessionActive = (authState = store.state, now = Date.now()) => {
  if (!authState?.isAuthenticated) {
    return false;
  }
  if (
    authState.authSessionExpiresAt === null ||
    authState.authSessionExpiresAt === undefined
  ) {
    return false;
  }
  const normalizedExpiry = normalizeAuthSessionExpiry(
    authState.authSessionExpiresAt
  );
  return normalizedExpiry !== null && normalizedExpiry > now;
};

export const resetWalletSessionState = ({
  clearError = true,
  clearStorage = false,
} = {}) => {
  invalidateAuthSession();
  if (clearStorage && typeof window !== "undefined") {
    try {
      window.sessionStorage?.clear();
    } catch {}
    clearMetaKeepSession();
  }

  store.commit("setConnectedStatus", false);
  store.commit("setAccounts", []);
  store.commit("setCurrentAccount", {});
  setCarbonSession();
  store.commit("setBalances", {});
  store.commit("setUSDCBalance", 0);
  store.commit("setBctBalance", 0);
  store.commit("setAdapter", null);
  store.commit("setProvider", null);
  store.commit("setWeb3Auth", null);
  store.commit("setWalletInfo", null);
  store.commit("setIsLogging", false);
  store.commit("setDialogMetamask", false);
  store.commit("setDialogMetaKeep", false);
  store.commit("setDialogWeb3Auth", false);
  store.commit("setDialogLedger", false);
  if (clearError) {
    store.commit("setWalletErrorMessage", "");
  }
};

const walletSessionAddressMatches = (actual, expected) =>
  ethers.utils.isAddress(actual || "") &&
  ethers.utils.isAddress(expected || "") &&
  actual.toLowerCase() === expected.toLowerCase();

const hasLiveMetaMaskSession = async (currentAccount) => {
  try {
    const provider = await getPreferredProvider();
    if (typeof provider?.request !== "function") {
      return false;
    }
    const accounts = await provider.request({ method: "eth_accounts" });
    const activeAddress = Array.isArray(accounts) ? accounts[0] : null;
    const trackedAddress = store.state.accounts?.[0];
    return (
      walletSessionAddressMatches(activeAddress, currentAccount.address) &&
      walletSessionAddressMatches(activeAddress, trackedAddress)
    );
  } catch {
    return false;
  }
};

const hasLiveMetaKeepSession = async (currentAccount) => {
  const ethersProvider = toRaw(currentAccount?.provider);
  const provider = toRaw(ethersProvider?.provider || ethersProvider);
  if (!provider || provider.connected === false) {
    return currentAccount?.sessionRestored === true
      ? await checkAuth()
      : false;
  }
  const activeAddress = Array.isArray(provider.accounts)
    ? provider.accounts[0]
    : null;
  return walletSessionAddressMatches(
    activeAddress,
    currentAccount?.authAddress
  );
};

export const enforceActiveWalletSession = async () => {
  if (!store.state.isAuthenticated) {
    return true;
  }
  const currentAccount = toRaw(store.state.currentAccount || {});
  let isLive = true;
  if (currentAccount.type === "MetaMask") {
    isLive = await hasLiveMetaMaskSession(currentAccount);
  } else if (currentAccount.type === "MetaKeep") {
    isLive = await hasLiveMetaKeepSession(currentAccount);
  }
  if (isLive) {
    return true;
  }

  const clearedSession = clearAuthenticatedWalletSession();
  resetWalletSessionState({ clearStorage: true });
  await clearedSession;
  return false;
};

export function useUpload() {
  const progress = ref(0);

  async function upload_with_put(formData, url, _headers, uploading) {
    if (!formData) {
      throw new TypeError("formData is required for upload");
    }
    if (!url) {
      throw new TypeError("url is required for upload");
    }
    if (!uploading || typeof uploading !== "object" || !("value" in uploading)) {
      throw new TypeError("uploading ref is required");
    }
    try {
      uploading.value = true;
      const response = await axios.put(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const loaded = progressEvent?.loaded ?? 0;
          const total = progressEvent?.total ?? 0;
          let percent = total
            ? Math.round((loaded * 100) / total)
            : 0;
          if (!Number.isFinite(loaded) || loaded < 0) {
            percent = 0;
          }
          progress.value = Math.min(100, Math.max(0, percent));
        },
      });
      uploading.value = false;
      progress.value = 0;
      return response;
    } catch (error) {
      uploading.value = false;
      progress.value = 0;
      throw error;
    }
  }

  return {
    progress,
    upload_with_put,
  };
}

const HOSTILE_KEYS = new Set(["__proto__", "constructor", "prototype"]);
const MAX_UINT64 = (1n << 64n) - 1n;
const DAY_IN_MS = 86400000;
const hasHostileKey = (obj) =>
  !!obj &&
  Object.getOwnPropertyNames(obj).some((key) => HOSTILE_KEYS.has(key));

const clampToUint64 = (value) => {
  if (!hasMeaningfulValue(value)) {
    return 0n;
  }
  if (typeof value === "bigint") {
    if (value < 0n) return 0n;
    return value > MAX_UINT64 ? MAX_UINT64 : value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new Error("Invalid claim timestamp value.");
    }
    const normalized = Math.trunc(value);
    if (normalized <= 0) return 0n;
    const big = BigInt(normalized);
    return big > MAX_UINT64 ? MAX_UINT64 : big;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return 0n;
    const big = BigInt(trimmed);
    if (big < 0n) return 0n;
    return big > MAX_UINT64 ? MAX_UINT64 : big;
  }
  throw new Error("Invalid claim timestamp value.");
};

export const stripHostileKeys = (value, seen = new WeakSet()) => {
  if (!hasMeaningfulValue(value)) {
    return value;
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) {
      return [];
    }
    seen.add(value);
    return value.map((entry) => stripHostileKeys(entry, seen));
  }
  if (!isPlainObject(value)) {
    return value;
  }
  if (seen.has(value)) {
    return {};
  }
  seen.add(value);
  const sanitized = {};
  Object.keys(value).forEach((key) => {
    if (HOSTILE_KEYS.has(key)) {
      return;
    }
    const next = value[key];
    if (Array.isArray(next) || isPlainObject(next)) {
      sanitized[key] = stripHostileKeys(next, seen);
    } else {
      sanitized[key] = next;
    }
  });
  return sanitized;
};

export const sanitizeHeadersPayload = (headers = {}) => {
  if (!headers || typeof headers !== "object") {
    throw new TypeError("headers must be provided as an object");
  }
  const sanitized = {};
  Object.entries(headers).forEach(([name, rawValue]) => {
    if (!HEADER_NAME_SAFE_REGEX.test(name)) {
      return;
    }
    if (!hasMeaningfulValue(rawValue)) {
      sanitized[name] = rawValue;
      return;
    }
    if (typeof rawValue !== "string") {
      sanitized[name] = rawValue;
      return;
    }
    if (HEADER_VALUE_FORBIDDEN_REGEX.test(rawValue)) {
      throw new Error(`Header ${name} contains forbidden characters`);
    }
    sanitized[name] = rawValue.slice(0, HEADER_VALUE_MAX_LENGTH);
  });
  return sanitized;
};

export const isCacheEntryValid = (entry, key, ttlMs = 300000) => {
  if (!entry || typeof entry !== "object" || !key) {
    return false;
  }
  const normalizedKey = key.toLowerCase?.() ?? key;
  const entryKey = entry.address?.toLowerCase?.() ?? entry.address;
  if (entryKey !== normalizedKey) {
    return false;
  }
  if (!Number.isFinite(entry.fetchedAt)) {
    return false;
  }
  if (!Number.isFinite(ttlMs) || ttlMs <= 0) {
    return true;
  }
  return Date.now() - entry.fetchedAt < ttlMs;
};

export const encodeClaimTimestampMatrix = ({
  maxYear,
  speciesCount,
  config = {},
}) => {
  const years = Number(maxYear);
  const speciesTotal = Number(speciesCount);
  if (!Number.isInteger(years) || years <= 0) {
    throw new Error("Invalid claim timestamp matrix dimensions.");
  }
  if (!Number.isInteger(speciesTotal) || speciesTotal <= 0) {
    throw new Error("Invalid claim timestamp matrix dimensions.");
  }
  const header = Buffer.alloc(32);
  header[0] = 1;
  header[1] = years;
  header[2] = speciesTotal;
  const entryCount = years * speciesTotal;
  if (!Number.isSafeInteger(entryCount) || entryCount > 20000) {
    throw new Error("Claim timestamp matrix too large.");
  }
  if (hasHostileKey(config)) {
    throw new Error("Invalid claim timestamp matrix key.");
  }
  const payload = Buffer.alloc(entryCount * 8);
  for (let year = 1; year <= years; year++) {
    const perSpecies = (config && config[year]) || {};
    if (hasHostileKey(perSpecies)) {
      throw new Error("Invalid claim timestamp matrix key.");
    }
    for (let spIndex = 0; spIndex < speciesTotal; spIndex++) {
      const idx = (year - 1) * speciesTotal + spIndex;
      const rawValue = perSpecies[spIndex];
      const value = clampToUint64(rawValue);
      payload.writeBigUInt64BE(value, idx * 8);
    }
  }
  return ethers.utils.hexlify(Buffer.concat([header, payload]));
};

export const sendPutRequest = async (
  urlEndpoint,
  jsonObject,
  _headers,
  role,
  retryCount = 0,
  authFn = authenticateWithMetaMask
) => {
  try {
    return await axios.put(urlEndpoint, jsonObject, {
      headers: { "Content-Type": "application/json" },
    });
  } catch (error) {
    if (error.response) {
      if (error.response.data.detail === "Challenge expired") {
        if (retryCount >= 1) {
          return;
        }
        await authFn(role);
        return await sendPutRequest(
          urlEndpoint,
          jsonObject,
          null,
          role,
          retryCount + 1,
          authFn
        );
      }
    }
  }
};

export const getJsonData = (form, keyList) => {
  if (!form || typeof form !== "object") {
    throw new TypeError("form must be provided as an object");
  }
  if (!hasMeaningfulValue(keyList)) {
    throw new TypeError("keyList must be provided");
  }
  const keys = Array.isArray(keyList) ? keyList : Array.from(keyList);
  const jsonData = {};
  keys.forEach((keyName) => {
    if (HOSTILE_KEYS.has(String(keyName))) {
      return;
    }
    if (Object.prototype.hasOwnProperty.call(form, keyName)) {
      jsonData[keyName] = form[keyName];
    } else {
      jsonData[keyName] = null;
    }
  });
  return jsonData;
};

const normalizeSleepDelay = (value) => {
  if (!hasMeaningfulValue(value)) {
    return 0;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return 0;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) {
      throw new TypeError("Delay must be a finite number.");
    }
    return parsed;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new TypeError("Delay must be a finite number.");
    }
    return value;
  }
  if (typeof value === "bigint") {
    return Number(value);
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    throw new TypeError("Delay must be a finite number.");
  }
  return parsed;
};

export const sleep = (ms) => {
  const normalizedDelay = normalizeSleepDelay(ms);
  const delay = normalizedDelay <= 0 ? 0 : normalizedDelay;
  return new Promise((resolve) => setTimeout(resolve, delay));
};

export const checkResponseHasSuccess = (response) => {
  if (!response || typeof response !== "object") {
    throw new TypeError("response must be an object");
  }
  const status = Number(response.status);
  return status >= 200 && status < 300;
};

const joinURLSegments = (segments) =>
  segments
    .map((segment) => {
      if (!hasMeaningfulValue(segment)) {
        return "";
      }
      return encodeURIComponent(String(segment));
    })
    .join("/");

const hasURLSegment = (segments) => segments.some(hasMeaningfulValue);

const resolveStringAssetURL = (rawValue, baseURL) => {
  const raw = rawValue.trim();
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }
  const normalized = raw.startsWith("./") ? raw.slice(2) : raw;
  if (/^\/(?:private\/)?tmp\//.test(normalized)) {
    return new URL(normalized, baseURL).toString();
  }
  // Other leading slashes are absolute application paths.
  if (normalized.startsWith("/")) {
    return normalized;
  }
  if (normalized.includes("/")) {
    return new URL(normalized, baseURL).toString();
  }
  return null;
};

export const getURLFromPieces = (...args) => {
  return new URL(
    joinURLSegments(args),
    process.env.VUE_APP_API_URL
  ).toString();
};

export const getImageURLFromPieces = (...args) => {
  if (!hasURLSegment(args)) {
    return null;
  }
  if (args.length === 1 && typeof args[0] === "string") {
    const resolved = resolveStringAssetURL(
      args[0],
      process.env.VUE_APP_BASE_IMAGE_URL
    );
    if (resolved !== null) {
      return resolved;
    }
  }
  return new URL(
    joinURLSegments(args),
    process.env.VUE_APP_BASE_IMAGE_URL
  ).toString();
};

export const getFileURLFromPieces = (...args) => {
  if (!hasURLSegment(args)) {
    return null;
  }
  if (args.length === 1 && typeof args[0] === "string") {
    const resolved = resolveStringAssetURL(
      args[0],
      process.env.VUE_APP_BASE_FILE_URL
    );
    if (resolved !== null) {
      return resolved;
    }
  }
  return new URL(
    joinURLSegments(args),
    process.env.VUE_APP_BASE_FILE_URL
  ).toString();
};

export const getVueAppEnv = () => {
  return process.env.VUE_APP_ENV;
};

const resolveAssetList = (pathList, resolver) =>
  pathList
    .split(",")
    .map((item) => resolver(item))
    .filter(hasMeaningfulValue);

export const processProjects = (projects) => {
  if (!Array.isArray(projects)) {
    throw new TypeError("projects must be provided as an array");
  }
  return projects.map((item = {}) => {
    const sanitizedItem = stripHostileKeys(item);
    const {
      project_photos_path,
      project_files_path,
      project_file1_path,
      project_file2_path,
      ...rest
    } = sanitizedItem;
    if (project_files_path !== null && typeof project_files_path === "string") {
      rest.project_files = resolveAssetList(project_files_path, getFileURLFromPieces);
    }
    if (project_file1_path !== null && typeof project_file1_path === "string") {
      rest.project_file1 = resolveAssetList(project_file1_path, getFileURLFromPieces)[0];
    }
    if (project_file2_path !== null && typeof project_file2_path === "string") {
      rest.project_file2 = resolveAssetList(project_file2_path, getFileURLFromPieces)[0];
    }
    if (
      project_photos_path !== null &&
      typeof project_photos_path === "string"
    ) {
      rest.project_photos = resolveAssetList(project_photos_path, getImageURLFromPieces);
    }
    return rest;
  });
};

export const authenticateWithChallenge = async (
  ethAddress,
  role,
  identifier = ""
) => {
  const payload = { role };
  const normalizedIdentifier =
    typeof identifier === "string" ? identifier.trim() : "";
  const normalizedAddress =
    typeof ethAddress === "string" ? ethAddress.trim() : "";
  if (normalizedIdentifier) {
    payload.identifier = normalizedIdentifier;
  }
  if (normalizedAddress) {
    payload.ethAddress = normalizedAddress;
  }
  if (!payload.identifier && !payload.ethAddress) {
    return null;
  }
  logDev("auth", "login request", {
    role,
    identifier: payload.identifier ? redactValue(payload.identifier, 3) : null,
    ethAddress: payload.ethAddress ? redactValue(payload.ethAddress) : null,
  });
  const response = await axios.post(
    getURLFromPieces("login"),
    payload,
    {
      headers: { "Content-Type": "application/json" },
    }
  );
  logDev("auth", "login response", {
    hasChallenge: Boolean(response?.data?.challenge),
    challengePrefix: response?.data?.challenge
      ? response.data.challenge.split("\n")[0]
      : null,
  });
  return response?.data?.challenge ?? null;
};

function applyAdminRole(adminRole) {
  store.commit("setAdminRole", adminRole ?? null);
}

const updateCurrentAccountRole = (role) => {
  if (role === null || role === undefined) {
    return;
  }
  const normalizedRole = Number(role);
  const current = store.state.currentAccount;
  if (!current || typeof current !== "object") {
    return;
  }
  if (Number(current.role) === normalizedRole) {
    return;
  }
  store.commit("setCurrentAccount", { ...current, role: normalizedRole });
};

const localTestnetMetaMaskFields = (currentAccount = {}) => {
  if (!isLocalTestnetMetaMaskEnabled() || currentAccount.type) {
    return {};
  }
  return {
    type: "MetaMask",
    walletId: currentAccount.walletId || "metamask",
  };
};

const canUseLocalTestnetMetaMaskAuth = () =>
  isLocalTestnetMetaMaskEnabled() &&
  Boolean(store.state.currentAccount?.address);

const sessionMatchesRole = (role) => {
  return authRoleMatches(store.state.currentAccount?.role, role);
};

const loadBackendSessionIdentity = async () => {
  // Session identity is part of authentication validation. Mark it as already
  // handled so a 401 cannot recursively start another authentication flow.
  const response = await axios.get(
    getURLFromPieces("v2", "session-identity"),
    { __authRetry: true }
  );
  return response?.data;
};

export const applyAuthToken = (accessToken, adminRole, expiresAt) => {
  if (!accessToken) {
    invalidateAuthSession();
    return false;
  }
  const normalizedExpiry = normalizeAuthSessionExpiry(expiresAt);
  if (normalizedExpiry === null || normalizedExpiry <= Date.now()) {
    invalidateAuthSession();
    return false;
  }
  axios.defaults.headers.common = axios.defaults.headers.common || {};
  axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  applyAuthSessionExpiry(normalizedExpiry);
  applyAdminRole(adminRole);
  return true;
};

const authenticateWithToken = async ({ challenge, signature }) => {
  if (!challenge || !signature) {
    return null;
  }
  const body = { challenge, signature };
  logDev("auth", "authenticate request", {
    challengePrefix: challenge.split("\n")[0],
    signature: redactValue(signature, 6),
  });
  const response = await axios.post(
    getURLFromPieces(AUTHENTICATE_ENDPOINT),
    body,
    { headers: { "Content-Type": "application/json" } }
  );
  logDev("auth", "authenticate response", {
    hasToken: Boolean(response?.data?.access_token),
    walletAddress: response?.data?.wallet_address
      ? redactValue(response.data.wallet_address)
      : null,
    expiresAt: response?.data?.expires_at || null,
  });
  return response?.data ?? null;
};

export const authenticateWithLedger = async (role) => {
  try {
    const address = store.state.currentAccount.address;
    logDev("auth", "ledger auth start", {
      role,
      address: address ? redactValue(address) : null,
    });
    const challenge = await authenticateWithChallenge(address, role);
    if (!challenge) {
      return false;
    }
    const signature = await signChallengeLedger(challenge);
    if (!signature) {
      return false;
    }
    const authResponse = await authenticateWithToken({
      challenge,
      signature,
    });
    const ok = applyAuthToken(
      authResponse?.access_token,
      authResponse?.admin_role,
      authResponse?.expires_at
    );
    if (ok) {
      updateCurrentAccountRole(role);
      if (authResponse?.wallet_address) {
        store.commit("setAccounts", [authResponse.wallet_address]);
        store.commit("setCurrentAccount", {
          ...store.state.currentAccount,
          address: authResponse.wallet_address,
        });
      }
    }
    logDev("auth", "ledger auth result", { ok });
    return ok;
  } catch (error) {
    logDev("auth", "ledger auth error", {
      message: error?.message || String(error),
    });
    return false;
  }
};

export const authenticateWithMetaMask = async (role) => {
  let address = "";
  const rejectCurrentAttempt = async (message) => {
    rememberRejectedAuthAttempt({ role, walletType: "metamask", address });
    await clearAuthenticatedWalletSession();
    resetWalletSessionState({ clearError: false });
    store.commit("setWalletErrorMessage", message);
  };
  try {
    console.log("[MM-AUTH] start", { role, isAuth: store.state.isAuthenticated, currentAddr: store.state.currentAccount?.address });
    await waitForAuthenticatedWalletSessionClear();
    if (!store.state.currentAccount?.address && !store.state.accounts?.[0]) {
      await store.dispatch("connectWallet", {
        walletProvider: await getPreferredProvider(),
        walletId: store.state.currentAccount?.walletId,
      });
    }
    const networkReady = await store.dispatch(
      "ensureNetwork",
      ACTIVE_CHAIN.chainId
    );
    if (!networkReady) {
      return false;
    }
    const selectedProvider = await getPreferredProvider();
    const initialAuthority = await resolveInjectedWalletAuthority({
      provider: selectedProvider,
      currentAccountAddress: store.state.currentAccount?.address,
      trackedAccounts: store.state.accounts,
    });
    address = initialAuthority.address;
    console.log("[MM-AUTH] calling /login", { address, role });
    logDev("auth", "metamask auth start", {
      role,
      address: address ? redactValue(address) : null,
    });
    const challenge = await authenticateWithChallenge(address, role);
    console.log("[MM-AUTH] /login returned", { hasChallenge: !!challenge });
    if (!challenge) {
      console.log("[MM-AUTH] FAIL: no challenge");
      await rejectCurrentAttempt(
        "Wallet authentication could not start. Try again."
      );
      return false;
    }
    const signature = await signChallenge(challenge, {
      provider: selectedProvider,
      expectedAddress: address,
    });
    console.log("[MM-AUTH] signed", { hasSig: !!signature });
    if (!signature) {
      console.log("[MM-AUTH] FAIL: no signature");
      await rejectCurrentAttempt("Signature request was rejected or cancelled.");
      return false;
    }
    await waitForAuthenticatedWalletSessionClear();
    const preAuthenticationAuthority = await resolveInjectedWalletAuthority({
      provider: selectedProvider,
      currentAccountAddress: store.state.currentAccount?.address,
      trackedAccounts: store.state.accounts,
    });
    requireMatchingWalletAddress(
      preAuthenticationAuthority.address,
      address,
      "Wallet account"
    );
    const authResponse = await authenticateWithToken({
      challenge,
      signature,
    });
    console.log("[MM-AUTH] /authenticate returned", {
      hasToken: !!authResponse?.access_token,
      walletAddress: authResponse?.wallet_address,
    });
    const finalAuthority = await resolveInjectedWalletAuthority({
      provider: selectedProvider,
      currentAccountAddress: store.state.currentAccount?.address,
      trackedAccounts: store.state.accounts,
    });
    requireMatchingWalletAddress(
      finalAuthority.address,
      address,
      "Wallet account"
    );
    requireMatchingWalletAddress(
      authResponse?.wallet_address,
      address,
      "Authenticated wallet address"
    );
    const ok = applyAuthToken(
      authResponse?.access_token,
      authResponse?.admin_role,
      authResponse?.expires_at
    );
    console.log("[MM-AUTH] applyAuthToken:", ok);
    if (ok) {
      let directBusinessWallet = null;
      if (
        isLocalTestnetMetaMaskEnabled() &&
        [
          AUTH_ROLES.ADMIN,
          AUTH_ROLES.DEVELOPER,
          AUTH_ROLES.AUDITOR,
          AUTH_ROLES.FARMER,
          AUTH_ROLES.INVESTOR,
        ].includes(Number(role))
      ) {
        const identity = await loadLocalDirectWalletSessionIdentity({
          expectedAuthWallet: authResponse?.wallet_address,
          expectedRole: Number(role),
        });
        directBusinessWallet = identity.businessWalletAddress;
      }
      forgetRejectedAuthAttempt({ role, walletType: "metamask", address });
      updateCurrentAccountRole(role);
      if (authResponse?.wallet_address) {
        console.log("[MM-AUTH] committing setAccounts + setCurrentAccount", { addr: authResponse.wallet_address });
        store.commit("setAccounts", [authResponse.wallet_address]);
        // Set both ``address`` and ``authAddress``. For MetaMask the signer
        // and the business wallet are the same key — consumers (e.g.
        // FarmerPage.vue header) read ``authAddress`` for display, and the
        // MetaKeep branch sets both fields too. Leaving authAddress undefined
        // makes ``currentAccount.authAddress.slice(0, 4)`` throw inside the
        // farmer dashboard render and swallow the whole subtree.
        store.commit("setCurrentAccount", {
          ...store.state.currentAccount,
          address: authResponse.wallet_address,
          authAddress: authResponse.wallet_address,
          ...(directBusinessWallet
            ? { businessWalletAddress: directBusinessWallet }
            : {}),
        });
      }
    }
    console.log("[MM-AUTH] done", { ok, finalIsAuth: store.state.isAuthenticated });
    logDev("auth", "metamask auth result", { ok });
    return ok;
  } catch (error) {
    console.log("[MM-AUTH] ERROR:", error?.message || String(error));
    const detailMessage = resolveWalletAuthErrorMessage(error);
    const message =
      role === 0 && /not authorized/i.test(detailMessage)
        ? "This wallet is not registered as an admin."
        : detailMessage || "MetaMask authentication failed.";
    rememberRejectedAuthAttempt({ role, walletType: "metamask", address });
    await clearAuthenticatedWalletSession();
    resetWalletSessionState({ clearError: false });
    store.commit("setWalletErrorMessage", message);
    logDev("auth", "metamask auth error", {
      message: error?.message || String(error),
    });
    return false;
  }
};

export const authenticateWithMetaKeep = async (role) => {
  const currentAccount = toRaw(store.state.currentAccount);
  const identifier = currentAccount?.email || currentAccount?.phone || "";
  const initialStoredSession = getStoredMetaKeepSession();
  const authAddress =
    currentAccount?.authAddress || initialStoredSession.authWalletAddress || null;
  const key = `${(authAddress || "").toLowerCase()}:${role || ""}`;
  if (metaKeepInflight.has(key)) {
    return await metaKeepInflight.get(key);
  }
  const inFlight = (async () => {
    let serverSessionActive = false;
    try {
      store.commit("setWalletErrorMessage", "");
      const requireWalletAddress = (value, label) => {
        const address = typeof value === "string" ? value.trim() : "";
        if (!address || !ethers.utils.isAddress(address)) {
          throw new Error(`${label} is missing or invalid.`);
        }
        return address;
      };
      const optionalWalletAddress = (value, label) => {
        if (value === null || value === undefined || value === "") {
          return null;
        }
        return requireWalletAddress(value, label);
      };
      const sameWallet = (left, right) =>
        left.toLowerCase() === right.toLowerCase();
      const accountBusinessWallet = optionalWalletAddress(
        currentAccount?.businessWalletAddress,
        "MetaKeep account business wallet"
      );
      const storedBusinessWallet = optionalWalletAddress(
        initialStoredSession.businessWalletAddress,
        "Stored MetaKeep business wallet"
      );
      if (
        accountBusinessWallet &&
        storedBusinessWallet &&
        !sameWallet(accountBusinessWallet, storedBusinessWallet)
      ) {
        throw new Error("MetaKeep business wallet mismatch.");
      }
      const cachedBusinessWallet =
        accountBusinessWallet || storedBusinessWallet || null;
      const accountAuthWallet = optionalWalletAddress(
        currentAccount?.authAddress,
        "MetaKeep account auth wallet"
      );
      const storedAuthWallet = optionalWalletAddress(
        initialStoredSession.authWalletAddress,
        "Stored MetaKeep auth wallet"
      );
      if (
        accountAuthWallet &&
        storedAuthWallet &&
        !sameWallet(accountAuthWallet, storedAuthWallet)
      ) {
        throw new Error("MetaKeep auth wallet mismatch.");
      }
      const expectedAuthWallet = requireWalletAddress(
        accountAuthWallet || storedAuthWallet,
        "MetaKeep auth wallet"
      );
      const connectedAddress = optionalWalletAddress(
        currentAccount?.address,
        "MetaKeep connected wallet"
      );
      if (
        connectedAddress &&
        (!cachedBusinessWallet ||
          !sameWallet(connectedAddress, cachedBusinessWallet))
      ) {
        throw new Error("MetaKeep connected business wallet mismatch.");
      }
      // MetaKeep's SDK signs the login challenge with the personal wallet.
      // Its injected transaction provider may represent the separate business
      // wallet, so the recovered `/authenticate` wallet is the signer proof.
      const signerAddress = requireWalletAddress(
        expectedAuthWallet,
        "MetaKeep personal wallet"
      );

      const applySessionIdentity = async () => {
        const identity = await loadBackendSessionIdentity();
        const identityAuthWallet = requireWalletAddress(
          identity?.authWalletAddress,
          "Backend MetaKeep auth wallet"
        );
        const identityBusinessWallet = requireWalletAddress(
          identity?.businessWalletAddress,
          "Backend MetaKeep business wallet"
        );
        if (!Number.isInteger(identity?.role) || identity.role !== Number(role)) {
          throw new Error("Backend MetaKeep role mismatch.");
        }
        if (!sameWallet(identityAuthWallet, signerAddress)) {
          throw new Error("Backend MetaKeep auth wallet mismatch.");
        }
        if (
          cachedBusinessWallet &&
          !sameWallet(identityBusinessWallet, cachedBusinessWallet)
        ) {
          throw new Error("Backend MetaKeep business wallet mismatch.");
        }
        store.commit("setConnectedStatus", true);
        store.commit("setAccounts", [identityBusinessWallet]);
        store.commit("setCurrentAccount", {
          ...currentAccount,
          address: identityBusinessWallet,
          businessWalletAddress: identityBusinessWallet,
          authAddress: identityAuthWallet,
          role,
        });
        persistMetaKeepSession({
          businessWalletAddress: identityBusinessWallet,
          authWalletAddress: identityAuthWallet,
        });
        setCarbonSession({
          businessWalletAddress: identityBusinessWallet,
          authWalletAddress: identityAuthWallet,
        });
        logDev("auth", "metakeep auth result", {
          ok: true,
          walletAddress: redactValue(identityBusinessWallet),
        });
      };

      const cookieValid = await checkAuth();
      if (cookieValid && sessionMatchesRole(role)) {
        logDev("auth", "metakeep auth skip (cached session)", { role });
        return true;
      }
      if (cookieValid) {
        await axios.post(getURLFromPieces(LOGOUT_ENDPOINT), null, {
          withCredentials: true,
        });
      }
      const { sdk } = currentAccount || {};
      if (!sdk) {
        logDev("auth", "metakeep interactive reconnect required", { role });
        return false;
      }
      if (!identifier) {
        throw new Error("Missing MetaKeep identifier.");
      }
      logDev("auth", "metakeep auth start", {
        role,
        identifier: redactValue(identifier, 3),
      });
      const challenge = await authenticateWithChallenge(
        signerAddress,
        role,
        identifier
      );
      if (!challenge) {
        throw new Error("MetaKeep login challenge missing.");
      }
      const signature = await signMetaKeepChallenge(
        sdk,
        currentAccount,
        challenge
      );
      const authResponse = await authenticateWithToken({
        challenge,
        signature,
      });
      serverSessionActive = true;
      const responseAuthWallet = requireWalletAddress(
        authResponse?.wallet_address,
        "Authenticated MetaKeep wallet"
      );
      if (!sameWallet(responseAuthWallet, signerAddress)) {
        throw new Error("Authenticated MetaKeep wallet mismatch.");
      }
      const ok = applyAuthToken(
        authResponse?.access_token,
        authResponse?.admin_role,
        authResponse?.expires_at
      );
      if (!ok) {
        throw new Error("MetaKeep authentication session is invalid.");
      }
      await applySessionIdentity();
      return true;
    } catch (e) {
      const message = resolveRequestErrorMessage(
        e,
        "MetaKeep authentication failed."
      );
      logDev("auth", "metakeep auth error", {
        message,
      });
      if (serverSessionActive) {
        await axios
          .post(getURLFromPieces(LOGOUT_ENDPOINT), null, {
            withCredentials: true,
          })
          .catch(() => {});
      }
      resetWalletSessionState({ clearError: false, clearStorage: true });
      store.commit("setWalletErrorMessage", message);
      console.log(e);
      return false;
    } finally {
      metaKeepInflight.delete(key);
    }
  })();
  metaKeepInflight.set(key, inFlight);
  return await inFlight;
};

const resolveMetaKeepErrorStatus = (error) => {
  if (!error || typeof error !== "object") {
    return null;
  }
  return (
    error.status ||
    error?.response?.data?.status ||
    error?.data?.status ||
    error?.error?.status ||
    null
  );
};

const refreshMetaKeepSession = async (currentAccount) => {
  const freshSdk = createMetaKeepSdk({
    email: currentAccount?.email,
    phone: currentAccount?.phone,
  });
  if (!freshSdk) {
    return null;
  }
  try {
    const web3Provider = await freshSdk.ethereum;
    const accounts = await web3Provider.enable();
    const refreshedAuthAddress = Array.isArray(accounts)
      ? accounts[0]
      : null;
    if (
      !walletSessionAddressMatches(
        refreshedAuthAddress,
        currentAccount?.authAddress
      )
    ) {
      throw new Error("Refreshed MetaKeep auth wallet mismatch.");
    }
    if (currentAccount && typeof currentAccount === "object") {
      const nextAccount = { ...currentAccount, sdk: freshSdk };
      if (web3Provider) {
        nextAccount.provider = new ethers.providers.Web3Provider(web3Provider);
        store.commit("setProvider", nextAccount.provider);
      }
      store.commit("setCurrentAccount", nextAccount);
    }
    return freshSdk;
  } catch (error) {
    console.log(error);
    return null;
  }
};

const signMetaKeepChallenge = async (sdk, currentAccount, challenge) => {
  if (!sdk || !challenge) {
    throw new Error("MetaKeep SDK or challenge is missing.");
  }
  const extractSignature = (payload) => {
    if (typeof payload === "string") {
      return payload;
    }
    if (payload && typeof payload.signature === "string") {
      return payload.signature;
    }
    return "";
  };
  try {
    const response = await sdk.signMessage(challenge, "Brazil Carbon login");
    const signature = extractSignature(response);
    if (!signature) {
      throw new Error("MetaKeep signature missing.");
    }
    return signature;
  } catch (error) {
    const status = resolveMetaKeepErrorStatus(error);
    if (status === "INVALID_SESSION") {
      const refreshedSdk = await refreshMetaKeepSession(currentAccount);
      if (refreshedSdk) {
        const response = await refreshedSdk.signMessage(
          challenge,
          "Brazil Carbon login"
        );
        const signature = extractSignature(response);
        if (!signature) {
          throw new Error("MetaKeep signature missing.");
        }
        return signature;
      }
    }
    throw error;
  }
};

export const authenticateWithWeb3Auth = async (role) => {
  try {
    if (!store.state.currentAccount) {
      return;
    }
    const address = store.state.currentAccount.address;
    logDev("auth", "web3auth auth start", {
      role,
      address: address ? redactValue(address) : null,
    });
    const challenge = await authenticateWithChallenge(address, role);
    if (!challenge) {
      return false;
    }
    const signature = await signChallenge(challenge);
    if (!signature) {
      return false;
    }
    const authResponse = await authenticateWithToken({
      challenge,
      signature,
    });
    const ok = applyAuthToken(
      authResponse?.access_token,
      authResponse?.admin_role,
      authResponse?.expires_at
    );
    if (ok) {
      updateCurrentAccountRole(role);
      if (authResponse?.wallet_address) {
        store.commit("setAccounts", [authResponse.wallet_address]);
        store.commit("setCurrentAccount", {
          ...store.state.currentAccount,
          address: authResponse.wallet_address,
        });
      }
    }
    logDev("auth", "web3auth auth result", { ok });
    return ok;
  } catch (error) {
    logDev("auth", "web3auth auth error", {
      message: error?.message || String(error),
    });
    return false;
  }
};

export const authHandlers = {
  metaMask: (role) => authenticateWithMetaMask(role),
  ledger: (role) => authenticateWithLedger(role),
  web3Auth: (role) => authenticateWithWeb3Auth(role),
  metaKeep: (role) => authenticateWithMetaKeep(role),
};

const resolveAuthWalletType = () => {
  if (store.state.dialogMetaKeep) return "metakeep";
  if (store.state.dialogWeb3Auth) return "web3auth";
  if (store.state.dialogLedger) return "ledger";
  if (store.state.dialogMetamask) return "metamask";
  return store.state.currentAccount?.walletId || "unknown";
};

const buildAuthKey = (role) => {
  const walletType = resolveAuthWalletType();
  const address = store.state.currentAccount?.address || "";
  return `${walletType}:${address.toLowerCase()}:${role ?? ""}`;
};

const currentRejectedAuthAttempt = (role) => {
  const walletType = resolveAuthWalletType();
  const address =
    store.state.currentAccount?.address ||
    store.state.accounts?.[0] ||
    "";
  return { role, walletType, address };
};

export const clearRejectedAuthAttempt = forgetRejectedAuthAttempt;

export const ensureAuthenticated = async ({
  role,
  force = false,
} = {}) => {
  console.log("[ENSURE-AUTH] start", {
    role,
    force,
    dialogMetamask: store.state.dialogMetamask,
    dialogLedger: store.state.dialogLedger,
    dialogWeb3Auth: store.state.dialogWeb3Auth,
    dialogMetaKeep: store.state.dialogMetaKeep,
    isAuth: store.state.isAuthenticated,
  });
  const key = buildAuthKey(role);
  if (authInflight.has(key)) {
    console.log("[ENSURE-AUTH] in-flight reuse", key);
    return await authInflight.get(key);
  }
  const inFlight = (async () => {
    if (!force) {
      const ok = await checkAuth();
      console.log("[ENSURE-AUTH] checkAuth result", ok);
      if (ok && sessionMatchesRole(role)) {
        store.commit("setIsAuthenticated", true);
        return true;
      }
      if (ok) {
        console.log("[ENSURE-AUTH] session role mismatch", {
          expected: role,
          actual: store.state.currentAccount?.role,
        });
        store.commit("setIsAuthenticated", false);
      }
    }

    let result = false;
    if (store.state.dialogMetamask) {
      console.log("[ENSURE-AUTH] routing to metaMask handler");
      result = await authHandlers.metaMask(role);
    } else if (store.state.dialogLedger) {
      result = await authHandlers.ledger(role);
    } else if (store.state.dialogWeb3Auth) {
      result = await authHandlers.web3Auth(role);
    } else if (store.state.dialogMetaKeep) {
      console.log("[ENSURE-AUTH] routing to metaKeep handler");
      result = await authHandlers.metaKeep(role);
    } else if (store.state.currentAccount?.type === "MetaKeep") {
      console.log("[ENSURE-AUTH] routing to metaKeep handler from currentAccount");
      result = await authHandlers.metaKeep(role);
    } else if (
      store.state.currentAccount?.type === "MetaMask" ||
      canUseLocalTestnetMetaMaskAuth()
    ) {
      console.log("[ENSURE-AUTH] routing to metaMask handler from currentAccount");
      result = await authHandlers.metaMask(role);
    } else if (store.state.currentAccount?.type === "Ledger") {
      console.log("[ENSURE-AUTH] routing to ledger handler from currentAccount");
      result = await authHandlers.ledger(role);
    } else if (store.state.currentAccount?.type === "Web3Auth") {
      console.log("[ENSURE-AUTH] routing to web3Auth handler from currentAccount");
      result = await authHandlers.web3Auth(role);
    } else {
      console.log("[ENSURE-AUTH] NO DIALOG FLAG SET — result stays false");
    }
    console.log("[ENSURE-AUTH] handler result", result);
    store.commit("setIsAuthenticated", result);
    if (result) {
      store.commit("setWalletErrorMessage", "");
    }
    return result;
  })().finally(() => {
    authInflight.delete(key);
  });

  authInflight.set(key, inFlight);
  return await inFlight;
};

export const requireAuthenticatedRole = async ({
  role,
  force = false,
} = {}) => {
  const authenticated = await ensureAuthenticated({ role, force });
  if (!authenticated) {
    return false;
  }
  if (!hasAuthenticatedRole(store.state, role)) {
    store.commit("setIsAuthenticated", false);
    return false;
  }
  return true;
};

export const signChallenge = async (
  challenge,
  { provider: injectedProvider, expectedAddress } = {}
) => {
  if (!challenge) {
    return null;
  }
  try {
    const provider = injectedProvider
      ? injectedProvider?.getSigner
        ? injectedProvider
        : new ethers.providers.Web3Provider(injectedProvider)
      : await getPreferredProviderEthers(
          store.state.currentAccount?.provider,
          store.state.currentAccount?.walletId
        );
    const signer = expectedAddress
      ? provider.getSigner(expectedAddress)
      : provider.getSigner();
    if (expectedAddress && typeof signer.getAddress === "function") {
      requireMatchingWalletAddress(
        await signer.getAddress(),
        expectedAddress,
        "Challenge signer address"
      );
    }
    return await signer.signMessage(challenge);
  } catch (error) {
    if (error?.code === 4001) {
      return null;
    }
    return null;
  }
};

export const signChallengeLedger = async (challenge) => {
  let transport;
  if (!challenge) {
    return null;
  }
  try {
    transport = await TransportWebUSB.create();
    const eth = new Eth(transport);
    const derivationPath = store.state.currentAccount.derivationPath;
    const signature = await eth.signPersonalMessage(
      derivationPath,
      Buffer.from(challenge).toString("hex")
    );
    return "0x" + signature.r + signature.s + signature.v.toString(16);
  } catch (error) {
    if (error?.code === 4001) {
      return null;
    }
    return null;
  }
};

export const removeAccents = (input) => {
  return input.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export const shouldUpdateOfferingStateInBackend = (offeringState, project) => {
  return (
    offeringState !== null &&
    project.offering.offering_state !== offeringState.value &&
    offeringState < OFFERING_STATES.NO_OFFERING
  );
};


export const getCreditStageIndexForNextYears = (stageIndex, year) => {
  const normalizedYear = Number(year);
  const normalizedStageIndex = Number(stageIndex);
  if (!Number.isFinite(normalizedYear) || !Number.isFinite(normalizedStageIndex)) {
    return stageIndex;
  }
  if (normalizedYear > 1) {
    return Math.max(normalizedStageIndex - 2, 0);
  }
  return normalizedStageIndex;
};

export const getCreditDisplayStageIndex = (stageIndex, year) => {
  const normalizedYear = Number(year);
  const normalizedStageIndex = Number(stageIndex);
  if (!Number.isFinite(normalizedYear) || !Number.isFinite(normalizedStageIndex)) {
    return stageIndex;
  }
  const offset = normalizedYear > 1 ? 2 : 0;
  return Math.max(normalizedStageIndex - offset, 0);
};

const parseDate = (dateString) => {
  const regex = /^(\d{2})-(\d{2})-(\d{4})$/;

  if (!regex.test(dateString)) {
    return null;
  }

  const [, day, month, year] = dateString.match(regex);

  if (day < 1 || day > 31 || month < 1 || month > 12) {
    return null;
  }

  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCDate() !== parseInt(day) ||
    date.getUTCMonth() + 1 !== parseInt(month) ||
    date.getUTCFullYear() !== parseInt(year)
  ) {
    return null;
  }

  return date;
};

export const isValidDate = (dateString) => {
  return !!parseDate(dateString);
};

export const isDateEarlier = (dateString) => {
  const inputDate = parseDate(dateString);

  if (!inputDate) {
    return false;
  }

  const today = new Date(
    Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate()
    )
  );

  return today > inputDate;
};

export const generatePaymentDates = (
  nextUnlockTime,
  paymentSlices,
  projectPeriodInDays,
  currentSlice,
  auditPeriod
) => {
  const slices = Number(paymentSlices);
  if (!Number.isFinite(slices) || slices <= 1) {
    return [];
  }

  const periodInDays = Number(projectPeriodInDays);
  if (!Number.isFinite(periodInDays) || periodInDays <= 0) {
    throw new Error("projectPeriodInDays must be a positive number");
  }

  const parsedUnlock = parseDateFromLocal(nextUnlockTime);
  if (!parsedUnlock || Number.isNaN(parsedUnlock.getTime())) {
    throw new Error("nextUnlockTime must be a valid date string");
  }

  const current = Number.isFinite(Number(currentSlice))
    ? Math.min(Math.max(1, Math.round(Number(currentSlice))), slices)
    : 1;
  const auditDays = Number.isFinite(Number(auditPeriod))
    ? Math.max(0, Number(auditPeriod))
    : 0;
  const adjustedUnlockTime = parsedUnlock.getTime() - auditDays * DAY_IN_MS;
  const intervalMs = (periodInDays / slices) * DAY_IN_MS;

  const dates = [];
  for (let sliceIndex = 2; sliceIndex <= slices; sliceIndex += 1) {
    const offsetMs = (sliceIndex - current) * intervalMs;
    const sliceDate = new Date(adjustedUnlockTime + offsetMs);
    dates.push(sliceDate.toLocaleString());
  }

  return dates;
};

export const generatePaymentDatesOld = (
  timestamp,
  daysToSubtract,
  paymentSlices
) => {
  const initialDate = new Date(timestamp);
  initialDate.setDate(initialDate.getDate() - daysToSubtract);

  const interval = paymentSlices
    ? daysToSubtract / (paymentSlices - 1)
    : daysToSubtract / 5;

  const paymentDates = [];
  const totalSlices = paymentSlices || 5;

  for (let i = 0; i < totalSlices - 1; i++) {
    const newDate = new Date(initialDate);
    newDate.setDate(initialDate.getDate() + Math.round(interval * i));
    paymentDates.push(newDate);
  }

  const newDate = new Date(initialDate);
  newDate.setDate(
    initialDate.getDate() +
      interval * (totalSlices - 1) +
      (interval % totalSlices)
  );
  paymentDates.push(newDate);

  if (paymentDates.length > 0) {
    paymentDates.shift();
  }

  return paymentDates.map((date) => date.toLocaleString());
};

export const featuredProjects = async ({
  currentAccount = null,
  filterByDeveloperId = null,
  filterByAuditorId = null,
  withoutCurrentAccount = false,
  listed = true,
}) => {
  const projectsToShow = [];
  const formData = [];
  const auditorProjects = [];
  const developerProjects = [];

  await store.dispatch("getProjectsFromBackend", {
    listed,
    endpoint: CARBON_PROJECT_ENDPOINT,
  });
  const projects = store.getters.projects;

  if (projects.length > 0) {
    const length = projects.length > 2 ? 2 : projects.length;
    for (let i = 0; i < projects.length; i++) {
      const project = projects[i];
      const projectToShow = reactive(JSON.parse(JSON.stringify(project)));
      if (projectsToShow.length >= length) {
        break;
      }

      if (!projectToShow.offering) {
        const isApproved = projectToShow.approved !== false;
        if (
          projectToShow?.public_offering &&
          projectToShow.public_offering.state === 3
        ) {
          continue;
        }
        const projectContractAddress = projectToShow.smart_contract_address || null;
        const hasSmartContractAddress = !!projectContractAddress;
        if (
          !projectToShow?.public_offering &&
          (!hasSmartContractAddress || !isApproved)
        ) {
          continue;
        }
        if (
          projectToShow.type !== PROJECT_TYPES.AGROFORESTRY &&
          !isCurrentV2CarbonProject(projectToShow) &&
          projectToShow.investment &&
          !projectToShow.public_offering &&
          projectContractAddress &&
          isApproved
        ) {
          if (projectToShow.public_offering) {
            projectToShow.isSale = true;
          }
          const yearInfo = await store
            .dispatch("callGetYearInfoInvestment", {
              contractAddress: projectContractAddress,
              version: projectToShow.version,
              project: projectToShow,
            })
            .catch(() => null);
          if (yearInfo && typeof yearInfo === "object") {
            const {
              currentYear: cY,
              currentStageIndex: cSI,
              projectCompleted: pC,
            } = yearInfo;
            if (cSI !== undefined && cSI !== null) {
              projectToShow.currentStageIndex = cSI;
            }
            if (cY !== undefined && cY !== null) {
              projectToShow.currentYear = cY;
            }
            if (pC !== undefined) {
              projectToShow.projectCompleted = pC;
            }
          }
        } else if (projectToShow.type === PROJECT_TYPES.AGROFORESTRY) {
          if (projectContractAddress && isApproved) {
            const yearInfo = await store
              .dispatch("callGetYearInfoAgroInvestment", {
                contractAddress: projectContractAddress,
                version: projectToShow.version,
                project: projectToShow,
              })
              .catch(() => null);
            if (yearInfo && typeof yearInfo === "object") {
              const {
                currentYear: cY,
                currentStageIndex: cSI,
                projectCompleted: pC,
              } = yearInfo;
              if (cSI !== undefined && cSI !== null) {
                projectToShow.currentStageIndex = cSI.toString();
              }
              if (cY !== undefined && cY !== null) {
                projectToShow.currentYear = cY.toString();
              }
              if (pC !== undefined) {
                projectToShow.projectCompleted = pC;
              }
            }
          }
        }
        if (
          filterByDeveloperId &&
          projectToShow.developer &&
          projectToShow.developer.wallet_address.toLowerCase() ===
            filterByDeveloperId.toLowerCase()
        ) {
          const progress =
            (projectToShow.supply - projectToShow.max_supply) /
            projectToShow.supply;
          projectToShow.progressPercentage = `${(progress * 100).toFixed(2)}%`;
          developerProjects.push(projectToShow);
        } else if (
          filterByAuditorId &&
          projectToShow.auditor &&
          projectToShow.auditor.wallet_address.toLowerCase() ===
            filterByAuditorId.toLowerCase()
        ) {
          const progress =
            (projectToShow.supply - projectToShow.max_supply) /
            projectToShow.supply;
          projectToShow.progressPercentage = `${(progress * 100).toFixed(2)}%`;
          developerProjects.push(projectToShow);
        } else {
          projectsToShow.push(projectToShow);
        }
        continue;
      }

      try {
        await store.dispatch("callGetOfferingState", {
          contractAddress: project.offering.smart_contract_address,
          networkId: project.offering.network_id,
        });
        projectToShow.offering.offering_state = store.state.offeringState;

        if (projectToShow.offering.offering_state >= PROJECT_ENDED) {
          await store.dispatch("getCurrentPaymentSlice", {
            contractAddress: project.offering.smart_contract_address,
          });
          await store.dispatch("callNextUnlockTimeFunction", {
            contractAddress: project.offering.smart_contract_address,
          });
          if (!projectToShow.offering?.project_period_in_days) {
            await store.dispatch("getListingEndFunction", {
              contractAddress: project.offering.smart_contract_address,
            });
            await store.dispatch("getPaymentSlices", {
              contractAddress: project.offering.smart_contract_address,
            });
            projectToShow.offering.payment_slices = store.state.paymentSlices;
          }
        }
        projectToShow.currentPaymentSlice = store.state.currentPaymentSlice;
        if (
          filterByAuditorId &&
          projectToShow.offering.auditorSigners?.some(
            (it) => it.toLowerCase() === currentAccount?.address?.toLowerCase()
          ) &&
          projectToShow.offering.offering_state >= PROJECT_ENDED
        ) {
          await store.dispatch("getAuditFunction", {
            contractAddress: project.offering.smart_contract_address,
            version: project.version,
          });
          projectToShow.auditWindowOpen = store.state.auditWindowOpen;
          projectToShow.successfulAudit = store.state.successfulAudit;
          projectToShow.unsuccessfulAudit = store.state.unsuccessfulAudit;
          projectToShow.auditPeriod = store.state.auditPeriod / 86400;
          if (projectToShow.offering.offering_state >= PROJECT_ENDED) {
            projectToShow.nextUnlockTime = store.state.nextUnlockTime;
            if (!projectToShow.offering?.project_period_in_days) {
              projectToShow.paymentDates = generatePaymentDatesOld(
                store.state.listingEnd,
                projectToShow.offering.listing_period_in_days,
                projectToShow.offering.payment_slices
              );
            } else {
              projectToShow.paymentDates = generatePaymentDates(
                store.state.nextUnlockTime,
                projectToShow.offering.payment_slices,
                projectToShow.offering.project_period_in_days,
                store.state.currentPaymentSlice,
                projectToShow.offering.audit_period_in_days
              );
            }
          }
          if (projectToShow.paymentDates.length > 0)
            auditorProjects.push(projectToShow);
        } else if (
          filterByDeveloperId &&
          projectToShow.offering.developerSigners?.some(
            (it) => it.toLowerCase() === currentAccount?.address?.toLowerCase()
          )
        ) {
          if (
            projectToShow.offering &&
            projectToShow.offering.offering_state >= PROJECT_ENDED
          ) {
            await store.dispatch("getUnsuccessfulAudit", {
              contractAddress: projectToShow.offering.smart_contract_address,
            });
            await store.dispatch("getAuditSuccessNumber", {
              contractAddress: projectToShow.offering.smart_contract_address,
              version: projectToShow.version,
            });
            projectToShow.successfulAudit = store.state.successfulAudit;
            projectToShow.unsuccessfulAudit = store.state.unsuccessfulAudit;
          }
          await store.dispatch(
            "fetchFundsCollected",
            project.offering.smart_contract_address
          );
          projectToShow.fundsCollected = store.state.fundsCollected;

          await store.dispatch("getRemainingDeveloperCommissionAmount", {
            contractAddress: project.offering.smart_contract_address,
          });
          projectToShow.remainingDeveloperCommission =
            store.state.remainingDeveloperCommission;

          await store.dispatch(
            "getRemainingDeveloperCarbonCreditCommissionAmount",
            {
              contractAddress: project.offering.smart_contract_address,
            }
          );
          projectToShow.remainingDeveloperCarbonCreditCommission =
            store.state.remainingDeveloperCarbonCreditCommission;

          const progress =
            projectToShow.fundsCollected /
            projectToShow.offering.listing_price /
            (projectToShow.offering.supply / 10 ** TOKEN_DECIMALS);
          projectToShow.progressPercentage = `${(progress * 100).toFixed(2)}%`;

          if (projectToShow.offering.offering_state >= PROJECT_ENDED) {
            projectToShow.nextUnlockTime = store.state.nextUnlockTime;
            if (!projectToShow.offering?.project_period_in_days) {
              projectToShow.paymentDates = generatePaymentDatesOld(
                store.state.listingEnd,
                projectToShow.offering.listing_period_in_days,
                projectToShow.offering.payment_slices
              );
            } else {
              projectToShow.paymentDates = generatePaymentDates(
                store.state.nextUnlockTime,
                projectToShow.offering.payment_slices,
                projectToShow.offering.project_period_in_days,
                store.state.currentPaymentSlice,
                projectToShow.offering.audit_period_in_days
              );
            }
          }
          if (projectToShow.paymentDates?.length > 0) {
            projectToShow.nexAuditDate =
              projectToShow.paymentDates[
                projectToShow.currentPaymentSlice === 2
                  ? 0
                  : projectToShow.currentPaymentSlice - 1
              ];
          }
          developerProjects.push(projectToShow);
        } else {
          if (
            projectToShow.offering.offering_state !==
              OFFERING_STATES.NO_OFFERING ||
            process.env.VUE_APP_ENV !== DEVELOPMENT_ENV
          ) {
            projectsToShow.push(projectToShow);
          }
        }
      } catch (error) {
        console.log(error);
        if (
          process.env.VUE_APP_ENV === DEVELOPMENT_ENV &&
          project.offering.network_id === ACTIVE_CHAIN.chainId
        ) {

        }
      }
    }

    if (withoutCurrentAccount) {
      const processedProjects = processProjects(projectsToShow);
      formData.push(...processedProjects);
    }
  }

  return { projectsToShow, auditorProjects, developerProjects, formData };
};

export const collectProjects = async ({
  currentAccount = null,
  filterByDeveloperId = null,
  filterByAuditorId = null,
  withoutCurrentAccount = false,
  listed = true,
  projects = [],
  investProjects = false,
  onlyAgro = false,
  onlySale = false,
  endpoint = null,
  page = null,
  limit = null,
  includeStats = true,
}) => {
  const CONCURRENCY = 2;
  const projectsToShow = [];
  const formData = [];
  const auditorProjects = [];
  const developerProjects = [];
  let stats = null;
  let projectIndex = null;
  let total = 0;
  await store.dispatch("callGetProvider");
  console.log("[collectProjects] onlyAgro=", onlyAgro, "investProjects=", investProjects, "onlySale=", onlySale);
  if (!projects.length) {
    const backendResult = await store.dispatch("getProjectsFromBackend", {
      listed,
      endpoint,
      paged: page !== null && limit !== null,
      page,
      limit,
      includeStats,
    });
    projects = store.getters.projects;
    total = backendResult?.total ?? projects.length;
    console.log("[collectProjects] projects:", projects.length, "types:", projects.map(p => p.type));
    if (backendResult?.stats) {
      stats = backendResult.stats;
      console.log("[collectProjects] stats:", stats);
    }
    projectIndex = backendResult?.projectIndex ?? null;
  }
  let saleExists = false;
  const resolveYearInfoContractAddress = (project) =>
    project?.smart_contract_address || null;
  const isProjectApprovedForYearInfo = (project) =>
    project?.approved !== false;
  const readV2ProtocolProjection = (project) => {
    const directProjection = project?.v2_protocol || project?.v2Protocol;
    if (directProjection && typeof directProjection === "object") {
      return directProjection;
    }
    const dataProjection = project?.data?.v2_protocol || project?.data?.v2Protocol;
    if (dataProjection && typeof dataProjection === "object") {
      return dataProjection;
    }
    return null;
  };
  const isV2CarbonInvestmentProjection = (project) =>
    project?.type === PROJECT_TYPES.CARBON_CREDIT &&
    Boolean(readV2ProtocolProjection(project)?.creationId);
  const _canFetchYearInfo = (project) =>
    isProjectApprovedForYearInfo(project) &&
    !!resolveYearInfoContractAddress(project);
  if (projects.length > 0) {
    for (let i = 0; i < projects.length; i += CONCURRENCY) {
      const batch = projects.slice(i, i + CONCURRENCY);
      await Promise.all(
        batch.map(async (p) => {
          try {
            const projectToShow = reactive(JSON.parse(JSON.stringify(p)));
            if (onlyAgro && projectToShow.type !== PROJECT_TYPES.AGROFORESTRY) {
              console.log("[collectProjects] onlyAgro filter: project", projectToShow.id, "type=", projectToShow.type, "→", projectToShow.type === PROJECT_TYPES.AGROFORESTRY ? "PASS" : "SKIP");
              return null;
            }
            if (
              investProjects &&
              !onlyAgro &&
              !isV2CarbonInvestmentProjection(projectToShow) &&
              (projectToShow?.sale_year ||
                projectToShow?.public_offering ||
                !projectToShow?.investment)
            ) {
              if (
                (projectToShow.investment &&
                  hasSignedIssuance(
                    projectToShow,
                    "Collect projects signed issuance"
                  )) ||
                (projectToShow.public_offering &&
                  projectToShow.public_offering?.state !== 0)
              ) {
                saleExists = true;
              }
              return null;
            }
            if (
              onlySale &&
              !(
                (projectToShow.investment &&
                  hasSignedIssuance(
                    projectToShow,
                    "Collect sale projects signed issuance"
                  )) ||
                (projectToShow?.public_offering &&
                  projectToShow.public_offering?.state !== 0)
              )
            ) {
              return null;
            }
            if (!projectToShow.offering) {
              if (
                filterByDeveloperId &&
                projectToShow.developer &&
                projectToShow.developer.wallet_address.toLowerCase() ===
                  filterByDeveloperId.toLowerCase()
              ) {
                const progress =
                  (projectToShow.supply - projectToShow.max_supply) /
                  projectToShow.supply;
                projectToShow.progressPercentage = `${(progress * 100).toFixed(
                  2
                )}%`;
                developerProjects.push(projectToShow);
              } else if (
                filterByAuditorId &&
                projectToShow?.auditor &&
                projectToShow.auditor.wallet_address.toLowerCase() ===
                  filterByAuditorId.toLowerCase()
              ) {
                const progress =
                  (projectToShow.supply - projectToShow.max_supply) /
                  projectToShow.supply;
                projectToShow.progressPercentage = `${(progress * 100).toFixed(
                  2
                )}%`;
                developerProjects.push(projectToShow);
              } else {
                projectsToShow.push(projectToShow);
              }

              if (projectToShow.type === PROJECT_TYPES.AGROFORESTRY) {
                // Use pre-indexed state from API response instead of RPC calls
                const draftData = projectToShow.data || projectToShow;
                const cY = draftData.currentYear ?? projectToShow.currentYear;
                const cSI = draftData.currentStageIndex ?? projectToShow.currentStageIndex;
                const pC = draftData.projectCompleted ?? projectToShow.projectCompleted;
                const pF = draftData.projectFailed ?? projectToShow.projectFailed;
                if (cSI != null) projectToShow.currentStageIndex = cSI;
                if (cY != null) projectToShow.currentYear = cY;
                if (pC !== undefined) projectToShow.projectCompleted = pC;
                if (pF !== undefined) projectToShow.projectFailed = pF;
              } else if (!projectToShow.public_offering) {
                // Use pre-indexed state from API investment object
                const inv = projectToShow.investment;
                if (inv) {
                  if (inv.currentStageIndex != null) projectToShow.currentStageIndex = inv.currentStageIndex;
                  if (inv.currentYear != null) projectToShow.currentYear = inv.currentYear;
                  if (inv.projectCompleted !== undefined) projectToShow.projectCompleted = inv.projectCompleted;
                }
              }
              return null;
            }

            try {
              if (p.offering?.offering_state != null) {
                projectToShow.offering.offering_state = p.offering.offering_state;
              } else {
                await store.dispatch("callGetOfferingState", {
                  contractAddress: p.offering.smart_contract_address,
                  networkId: p.offering.network_id,
                });
                projectToShow.offering.offering_state = store.state.offeringState;
              }

              if (projectToShow.offering.offering_state >= PROJECT_ENDED) {
                await store.dispatch("getCurrentPaymentSlice", {
                  contractAddress: p.offering.smart_contract_address,
                });
                await store.dispatch("callNextUnlockTimeFunction", {
                  contractAddress: p.offering.smart_contract_address,
                });
                if (!projectToShow.offering?.project_period_in_days) {
                  await store.dispatch("getListingEndFunction", {
                    contractAddress: p.offering.smart_contract_address,
                  });
                  await store.dispatch("getPaymentSlices", {
                    contractAddress: p.offering.smart_contract_address,
                  });
                  projectToShow.offering.payment_slices =
                    store.state.paymentSlices;
                }
              }
              projectToShow.currentPaymentSlice =
                store.state.currentPaymentSlice;
              if (
                filterByAuditorId &&
                projectToShow.offering.auditorSigners?.some(
                  (it) =>
                    it.toLowerCase() === currentAccount?.address?.toLowerCase()
                ) &&
                projectToShow.offering.offering_state >= PROJECT_ENDED
              ) {
                await store.dispatch("getAuditFunction", {
                  contractAddress: p.offering.smart_contract_address,
                  version: p.version,
                });
                projectToShow.auditWindowOpen = store.state.auditWindowOpen;
                projectToShow.successfulAudit = store.state.successfulAudit;
                projectToShow.unsuccessfulAudit = store.state.unsuccessfulAudit;
                projectToShow.auditPeriod = store.state.auditPeriod / 86400;
                if (projectToShow.offering.offering_state >= PROJECT_ENDED) {
                  projectToShow.nextUnlockTime = store.state.nextUnlockTime;
                  if (!projectToShow.offering?.project_period_in_days) {
                    projectToShow.paymentDates = generatePaymentDatesOld(
                      store.state.listingEnd,
                      projectToShow.offering.listing_period_in_days,
                      projectToShow.offering.payment_slices
                    );
                  } else {
                    projectToShow.paymentDates = generatePaymentDates(
                      store.state.nextUnlockTime,
                      projectToShow.offering.payment_slices,
                      projectToShow.offering.project_period_in_days,
                      store.state.currentPaymentSlice,
                      projectToShow.offering.audit_period_in_days
                    );
                  }
                }
                if (projectToShow.paymentDates.length > 0)
                  auditorProjects.push(projectToShow);
              } else if (
                filterByDeveloperId &&
                projectToShow.offering.developerSigners?.some(
                  (it) =>
                    it.toLowerCase() === currentAccount?.address?.toLowerCase()
                )
              ) {
                if (
                  projectToShow.offering &&
                  projectToShow.offering.offering_state >= PROJECT_ENDED
                ) {
                  await store.dispatch("getUnsuccessfulAudit", {
                    contractAddress:
                      projectToShow.offering.smart_contract_address,
                  });
                  await store.dispatch("getAuditSuccessNumber", {
                    contractAddress:
                      projectToShow.offering.smart_contract_address,
                    version: projectToShow.version,
                  });
                  projectToShow.successfulAudit = store.state.successfulAudit;
                  projectToShow.unsuccessfulAudit =
                    store.state.unsuccessfulAudit;
                  if (store.state.unsuccessfulAudit) {
                    projectToShow.developerTokensBurned = false;
                  }
                }
                await store.dispatch(
                  "fetchFundsCollected",
                  p.offering.smart_contract_address
                );
                projectToShow.fundsCollected = store.state.fundsCollected;

                await store.dispatch("getRemainingDeveloperCommissionAmount", {
                  contractAddress: p.offering.smart_contract_address,
                });
                projectToShow.remainingDeveloperCommission =
                  store.state.remainingDeveloperCommission;

                await store.dispatch(
                  "getRemainingDeveloperCarbonCreditCommissionAmount",
                  {
                    contractAddress: p.offering.smart_contract_address,
                  }
                );
                projectToShow.remainingDeveloperCarbonCreditCommission =
                  store.state.remainingDeveloperCarbonCreditCommission;

                const progress =
                  projectToShow.fundsCollected /
                  projectToShow.offering.listing_price /
                  (projectToShow.offering.supply / 10 ** TOKEN_DECIMALS);
                projectToShow.progressPercentage = `${(progress * 100).toFixed(
                  2
                )}%`;

                if (projectToShow.offering.offering_state >= PROJECT_ENDED) {
                  projectToShow.nextUnlockTime = store.state.nextUnlockTime;
                  if (!projectToShow.offering?.project_period_in_days) {
                    projectToShow.paymentDates = generatePaymentDatesOld(
                      store.state.listingEnd,
                      projectToShow.offering.listing_period_in_days,
                      projectToShow.offering.payment_slices
                    );
                  } else {
                    projectToShow.paymentDates = generatePaymentDates(
                      store.state.nextUnlockTime,
                      projectToShow.offering.payment_slices,
                      projectToShow.offering.project_period_in_days,
                      store.state.currentPaymentSlice,
                      projectToShow.offering.audit_period_in_days
                    );
                  }
                }
                if (projectToShow.paymentDates?.length > 0) {
                  projectToShow.nexAuditDate =
                    projectToShow.paymentDates[
                      projectToShow.currentPaymentSlice === 2
                        ? 0
                        : projectToShow.currentPaymentSlice - 1
                    ];
                }
                developerProjects.push(projectToShow);
              } else {
                if (
                  projectToShow.offering.offering_state !==
                    OFFERING_STATES.NO_OFFERING ||
                  process.env.VUE_APP_ENV !== DEVELOPMENT_ENV
                ) {
                  projectsToShow.push(projectToShow);
                }
              }
            } catch (error) {
              console.log(error);
              if (
                process.env.VUE_APP_ENV === DEVELOPMENT_ENV &&
                p.offering.network_id === ACTIVE_CHAIN.chainId
              ) {
                return null;
              }
            }
          } catch (e) {
            console.log(e);
          }
        })
      );
    }

    if (withoutCurrentAccount) {
      const processedProjects = processProjects(projectsToShow);
      formData.push(...processedProjects);
    }
  }

  return {
    projectsToShow,
    auditorProjects,
    developerProjects,
    formData,
    saleExists,
    stats,
    projectIndex,
    total,
  };
};

export async function fetchSignInfoStage(
  currentAccount,
  investmentAddress,
  version,
  refetch = false,
  project = null
) {
  try {
    if (isCurrentV2CarbonProject(project)) {
      return;
    }
    if (store.state.signInfo[investmentAddress] && !refetch) {
      return;
    }
    if (!investmentAddress) {
      return;
    }
    const provider = await store.dispatch("callGetProvider");
    let stage;
    const projectAbi = ABI_REGISTRY.BrazilCarbonCreditUnified;
    const projectContract = new ethers.Contract(
      investmentAddress,
      projectAbi.abi,
      provider
    );
    const [nextSignYear, nextSignStageIndex] = await Promise.all([
      projectContract.nextSignYear(),
      projectContract.nextSignStageIndex(),
    ]);
    const stageInfo = await projectContract.getStageInfo(
      nextSignYear,
      nextSignStageIndex
    );
    const costRaw = stageInfo.cost;
    const raisedRaw = stageInfo.raised;
    const costBn = BigNumber.from(costRaw);
    const raisedBn = BigNumber.from(raisedRaw);
    const completed = !costBn.isZero() && raisedBn.gte(costBn);
    const signYear = Number(nextSignYear.toString());
    const signStageIndex = Number(nextSignStageIndex.toString());
    const signYearData = project?.investment?.yearly_costs?.find(
      (row) => row.year === signYear
    );
    const stageName = signYearData?.stage_data?.[signStageIndex]?.stage_name;
    if (!stageName) {
      throw new Error(
        `Missing carbon sign stage data for year ${signYear} stage ${signStageIndex}.`
      );
    }
    stage = {
      nextSignStageIndex: signStageIndex,
      nextSignYear: signYear,
      stage: signStageIndex,
      nameToLocal: stageName.toLowerCase(),
      stage_name: stageName,
      cost: ethers.utils.formatUnits(costRaw, 6),
      raised: ethers.utils.formatUnits(raisedRaw, 6),
      completed: completed,
      originalYear: nextSignYear.toString(),
      stagePrice: ethers.utils.formatUnits(stageInfo.price, 6),
      audited: stageInfo.audited,
      withdrawn: stageInfo.withdrawn,
      signed: stageInfo.signed,
    };
    store.commit("setSignInfo", {
      investmentAddress,
      stage,
    });
    return {
      stage,
      nextSignYear: Number(nextSignYear),
      nextSignStageIndex: Number(nextSignStageIndex),
    };
  } catch (e) {
    console.log(e);
  }
}

export async function fetchInvestmentProjects(currentAccount) {
  try {
    if (!currentAccount) return [];

    const [carbonResponse, agroResponse] = await Promise.all([
      store.dispatch("getProjectsFromBackend", {
        listed: true,
        endpoint: CARBON_PROJECT_ENDPOINT,
      }),
      store.dispatch("getProjectsFromBackend", {
        listed: true,
        endpoint: AGROFORESTRY_PROJECT_ENDPOINT,
      }),
    ]);
    if (!Array.isArray(carbonResponse?.projects)) {
      throw new Error("Auditor carbon project response is missing projects.");
    }
    if (!Array.isArray(agroResponse?.projects)) {
      throw new Error("Auditor agro project response is missing projects.");
    }
    const allProjects = [
      ...carbonResponse.projects,
      ...agroResponse.projects,
    ];
    const investmentProjects = allProjects.filter(
      (project) => project.investment
    );
    const projects = [];
    let provider = null;

    for (const project of investmentProjects) {
      try {
        if (isCurrentV2CarbonProject(project)) {
          if (
            !isAssignedV2ProjectCertifier(project, currentAccount.address)
          ) {
            continue;
          }
          const protocol = project.data.v2_protocol;
          const projectContractAddress = protocol.projectAddress;
          const detailResponse = await axios.get(
            getURLFromPieces(CARBON_PROJECT_ENDPOINT, String(project.id))
          );
          const detailedProject = detailResponse?.data;
          const detailedProtocol = detailedProject?.data?.v2_protocol;
          if (
            !isCurrentV2CarbonProject(detailedProject) ||
            String(detailedProtocol.projectAddress).toLowerCase() !==
              String(protocol.projectAddress).toLowerCase() ||
            String(detailedProtocol.certifier).toLowerCase() !==
              String(protocol.certifier).toLowerCase() ||
            String(detailedProtocol.semanticConfigHash).toLowerCase() !==
              String(protocol.semanticConfigHash).toLowerCase()
          ) {
            throw new Error(
              "Auditor Carbon V2 project detail does not match the listed protocol authority."
            );
          }
          const [processedP] = processProjects([detailedProject]);
          projects.push({
            ...processedP,
            auditRelatedData: {
              isCurrentV2: true,
              contractAddress: projectContractAddress,
              projectContractAddress,
              name: detailedProject.name,
              auditor: protocol.certifier,
              developer: protocol.developer,
              stageFunding: {},
              projectFailed: project.projectFailed === true,
              projectCompleted: false,
              auditCompleted: false,
            },
          });
          continue;
        }
        let investmentContractAbi;
        if (project.type === PROJECT_TYPES.AGROFORESTRY) {
          investmentContractAbi = ABI_REGISTRY.AgroforestryProject;
        } else if (project.type === PROJECT_TYPES.CARBON_CREDIT) {
          investmentContractAbi = ABI_REGISTRY.BrazilCarbonCreditUnified;
        } else {
          throw new Error(`Unsupported auditor project type: ${project.type}`);
        }
        const investmentContractAddress = project.smart_contract_address;
        const projectContractAddress = project.smart_contract_address;
        const isAssignedAuditor = isAssignedProjectAuditor(
          project,
          currentAccount.address
        );
        if (
          !investmentContractAddress ||
          !projectContractAddress ||
          !isAssignedAuditor
        )
          continue;

        if (!provider) {
          provider = await store.dispatch("callGetProvider");
        }
        const investmentContract = new ethers.Contract(
          investmentContractAddress,
          investmentContractAbi.abi,
          provider
        );

        const dbYearlyCosts = project.investment.yearly_costs;
        if (!Array.isArray(dbYearlyCosts)) {
          throw new Error(
            `Auditor project ${project.id} is missing investment yearly costs.`
          );
        }

        const [
          nextAuditYear,
          nextAuditStageIndex,
          projectFailed,
          projectCompleted,
          maxProjectYear,
        ] = await Promise.all([
          investmentContract.nextAuditYear(),
          investmentContract.nextAuditStageIndex(),
          investmentContract.projectFailed(),
          investmentContract.projectCompleted(),
          investmentContract.maxProjectYear(),
        ]);
        const auditYear = Number(
          nextAuditYear.toNumber ? nextAuditYear.toNumber() : nextAuditYear
        );
        const maxYear = Number(
          maxProjectYear.toNumber ? maxProjectYear.toNumber() : maxProjectYear
        );
        const auditCompleted = auditYear > maxYear;
        let stageFunding = {};
        if (!projectFailed && !auditCompleted) {
          const stageIndex = Number(
            nextAuditStageIndex.toNumber
              ? nextAuditStageIndex.toNumber()
              : nextAuditStageIndex
          );
          const originalYear = auditYear;
          const stageInfo = await investmentContract.getStageInfo(
            nextAuditYear,
            nextAuditStageIndex
          );
          const costBn = BigNumber.from(stageInfo.cost);
          const raisedBn = BigNumber.from(stageInfo.raised);
          const dbYearData = dbYearlyCosts.find(
            (year) => year.year === originalYear
          );
          if (!dbYearData || !Array.isArray(dbYearData.stage_data)) {
            throw new Error(
              `Missing audit stage data for project ${project.id} year ${originalYear}.`
            );
          }
          const dbStageData = dbYearData.stage_data[stageIndex];
          if (
            !dbStageData ||
            typeof dbStageData.stage_name !== "string" ||
            !dbStageData.stage_name.trim()
          ) {
            throw new Error(
              `Missing audit stage name for project ${project.id} year ${originalYear} stage ${stageIndex}.`
            );
          }
          const stageName = dbStageData.stage_name.trim();
          stageFunding = {
            name: stageName,
            stage: stageIndex,
            nameToLocal: stageName.toLowerCase(),
            cost: ethers.utils.formatUnits(stageInfo.cost, 6),
            raised: ethers.utils.formatUnits(stageInfo.raised, 6),
            completed: !costBn.isZero() && raisedBn.gte(costBn),
            audited: stageInfo.audited,
            withdrawn: stageInfo.withdrawn,
            signed: stageInfo.signed,
            originalYear,
            year: originalYear,
            canAuditAuditor: isAssignedAuditor,
            canAudit:
              isAssignedAuditor &&
              stageInfo.signed &&
              !costBn.isZero() &&
              raisedBn.gte(costBn),
            projectFailed,
          };
        }
        const [processedP] = processProjects([project]);
        projects.push({
          ...processedP,
          auditRelatedData: {
            contractAddress: investmentContractAddress,
            projectContractAddress,
            name:
              project.name ||
              `Investment Project - ${investmentContractAddress.slice(
                0,
                6
              )}...`,
            auditor: project.auditor,
            developer: project.developer,
            stageFunding,
            projectFailed,
            projectCompleted,
            auditCompleted,
          },
        });
      } catch (error) {
        console.error("Error loading auditor investment project:", error);
        throw error;
      }
    }
    return projects;
  } catch (error) {
    console.error("Error fetching investment projects:", error);
    throw error;
  }
}

export async function fetchDeveloperProjects({ currentAccount, id }) {
  try {
    if (!currentAccount?.address) return [];

    await store.dispatch("getProjectsFromBackend", {
      listed: false,
      is_hidden: true,
      endpoint: CARBON_PROJECT_ENDPOINT,
    });
    let allProjects;
    if (id) {
      allProjects = store.getters.projects.filter((it) => it.id === id);
    } else {
      allProjects = store.getters.projects;
    }
    const projects = [];

    for (const project of allProjects) {
      try {
        const investmentContractAddress = project.smart_contract_address;
        const projectContractAddress = project.smart_contract_address;
        const isCurrentV2Project = isCurrentV2CarbonProject(project);
        if (
          (!project.created_by ||
            project.created_by.wallet_address.toLowerCase() !==
              currentAccount.address.toLowerCase()) &&
          (!project.developer ||
            project.developer.wallet_address.toLowerCase() !==
              currentAccount.address.toLowerCase())
        )
          continue;
        let commission = 0;
        if (!!project.smart_contract_address?.length && !isCurrentV2Project) {
          const lastIssuance = readLatestIssuance(
            project,
            "Developer project latest issuance"
          );
          const brazilCarbonProjectABI = ABI_REGISTRY.BrazilCarbonCreditUnified;
          const provider = await store.dispatch("callGetProvider");
          const contract = new ethers.Contract(
            project.smart_contract_address,
            brazilCarbonProjectABI.abi,
            provider
          );
          commission = Number(await contract.investorFee());
          let response,
            buyerCreditsOnSale,
            buyerYearlyCredits;
          if (lastIssuance) {
            response = await store.dispatch("getPurchasedAmount", {
              contractAddress: project.smart_contract_address,
              maxYear: getCarbonTimelineHorizon(project),
              version: project.version,
              project,
            });
            buyerYearlyCredits = response.buyerYearlyCredits;
            buyerCreditsOnSale = response.buyerCreditsOnSale;
            project.totalBuyerCredits = Object.keys(buyerCreditsOnSale).reduce(
              (acc, year) => {
                acc[year] =
                  (buyerYearlyCredits[year] || 0) +
                  (buyerCreditsOnSale[year] || 0);
                return acc;
              },
              {}
            );
            project.lastIssuance = lastIssuance;
          }
        }

        const [processedP] = processProjects([project]);
        projects.push({
          ...processedP,
          developerRelatedData: {
            contractAddress: investmentContractAddress,
            projectContractAddress,
            name:
              project.name ||
              `Investment Project - ${investmentContractAddress?.slice(
                0,
                6
              )}...`,
            auditor: project.auditor,
            developer: project.developer,
            maxProjectYear: getCarbonTimelineHorizon(project),
            commission,
          },
        });
      } catch (error) {
        const [processedP] = processProjects([project]);
        projects.push({
          ...processedP,
          developerRelatedData: {
            contractAddress: project.smart_contract_address,
            projectContractAddress: project.smart_contract_address,
            name:
              project.name ||
              `Investment Project - ${project.smart_contract_address?.slice(
                0,
                6
              )}...`,
            auditor: project.auditor,
            developer: project.developer,
            maxProjectYear: getCarbonTimelineHorizon(project),
            commission: 0,
          },
        });
        console.warn("Failed to enrich developer project data", error);
      }
    }

    return projects;
  } catch (error) {
    console.error("Error fetching investment projects:", error);
    return [];
  }
}
export const showErrorMessage = ($q, errorMessage) => {
  createAlertNotify({
    title: "Error",
    message: errorMessage,
    icon: "error_outline",
    iconColor: "#2B2829",
    classes: "bc-alert bc-alert--error",
    timeout: 6000,
  });
};

export const showInfoMessage = ($q, infoMessage) => {
  createAlertNotify({
    title: "Info",
    message: infoMessage,
    icon: "info_outline",
    iconColor: "#2B2829",
    classes: "bc-alert bc-alert--neutral",
  });
};

export const showWarningMessage = ($q, warningMessage) => {
  createAlertNotify({
    title: "Warning",
    message: warningMessage,
    icon: "warning_amber",
    iconColor: "#8C5C00",
    classes: "bc-alert bc-alert--warning",
  });
};

export const showSuccessMessage = ($q, successMessage) => {
  createAlertNotify({
    title: "Success",
    message: successMessage,
    icon: "check",
    iconColor: "#0E6B3E",
    classes: "bc-alert bc-alert--success",
    timeout: 6000,
  });
};

const createAlertNotify = ({
  title,
  message,
  icon,
  iconColor,
  classes = "bc-alert bc-alert--neutral",
  timeout = 5000,
}) => {
  const notifyOptions = {
    position: "top",
    timeout,
    progress: true,
    group: false,
    message: String(title || "Notice"),
    classes,
    actions: [
      {
        icon: "close",
        color: "grey-8",
        flat: true,
        round: true,
      },
    ],
  };

  notifyOptions.caption = String(message || "");
  notifyOptions.icon = icon;
  notifyOptions.iconColor = iconColor;

  Notify.create(notifyOptions);
};

export const refreshAuthentication = async (
  _headers,
  connectDialog,
  isAuthenticated,
  role
) => {
  return await requireAuthenticatedRole({ role });
};


export const onMountedFlow = async (
  connectDialog,
  isAuthenticated,
  callback,
  show = 1,
  notShowDialog = false,
  role = null
) => {
  console.log("[FARMER-LOGIN] onMountedFlow()", { isAuthenticated: isAuthenticated.value, show, role });
  // Reuse only an exact live server session. ``checkAuth`` restores MetaKeep
  // identity from the backend after matching both persisted wallet addresses.
  if (!isAuthenticated.value) {
    try {
      const ok = await checkAuth();
      console.log("[FARMER-LOGIN] onMountedFlow checkAuth=", ok);
      if (ok && sessionMatchesRole(role)) {
        store.commit("setIsAuthenticated", true);
      } else if (ok) {
        console.log("[FARMER-LOGIN] onMountedFlow role mismatch", {
          expected: role,
          actual: store.state.currentAccount?.role,
        });
        store.commit("setIsAuthenticated", false);
      }
    } catch (e) {
      console.log("[FARMER-LOGIN] onMountedFlow checkAuth error", e?.message);
    }
  }
  if (isAuthenticated.value && sessionMatchesRole(role)) {
    console.log("[FARMER-LOGIN] onMountedFlow -> already authenticated, calling callback");
    await callback();
  } else {
    if (isAuthenticated.value) {
      store.commit("setIsAuthenticated", false);
    }
    console.log("[FARMER-LOGIN] onMountedFlow -> not authenticated, showing dialog");
    if (
      !notShowDialog &&
      connectDialog.value &&
      typeof connectDialog.value.show === "function"
    ) {
      connectDialog.value.show(show);
    }
  }
};

export const watchFlowForAuthenticate = async (
  newValue,
  oldValue,
  _connectDialog,
  isAuthenticated,
  role,
  callback
) => {
  try {
    if (
      shouldDeferAuthenticationWatch({
        isLogging: store.state.isLogging,
        connectingMetaKeep: store.state.connectingMetaKeep,
        isAuthenticated: isAuthenticated.value,
      })
    ) {
      return null;
    }
    if (!newValue?.address) {
      store.commit("setIsAuthenticated", false);
      return false;
    }
    if (isAuthenticated.value && sessionMatchesRole(role)) {
      await callback();
      return true;
    }
    const rejectedAttempt = currentRejectedAuthAttempt(role);
    if (
      hasRejectedAuthAttempt(rejectedAttempt) &&
      store.getters.walletErrorMessage
    ) {
      return false;
    }
    if (
      newValue &&
      oldValue &&
      (newValue.address !== oldValue.address ||
        !isAuthenticated.value) &&
      !store.state.isLogging
    ) {
      if (
        _connectDialog?.value &&
        typeof _connectDialog.value.show === "function"
      ) {
        await _connectDialog.value.show();
      }
      return false;
    }
    return Boolean(isAuthenticated.value && sessionMatchesRole(role));
  } catch (e) {
    console.error("[AUTH] watchFlowForAuthenticate failed", e);
    throw e;
  }
};

export const resolveRequestErrorMessage = (
  error,
  fallback = "Unexpected error occurred, please try again later."
) => {
  const detail = error?.response?.data?.detail;
  if (
    detail &&
    typeof detail === "object" &&
    typeof detail.message === "string" &&
    detail.message.trim().length > 0
  ) {
    return detail.message.trim();
  }
  if (typeof detail === "string" && detail.trim().length > 0) {
    return detail.trim();
  }
  const message = error?.response?.data?.message;
  if (typeof message === "string" && message.trim().length > 0) {
    return message.trim();
  }
  if (typeof error?.message === "string" && error.message.trim().length > 0) {
    return error.message.trim();
  }
  return fallback;
};

export const isAuthOrRoleFailure = (error) => {
  const status = Number(error?.response?.status);
  if ([400, 401, 403].includes(status)) {
    return true;
  }
  const backendStatus =
    error?.response?.data?.status ||
    error?.data?.status ||
    error?.status ||
    "";
  if (backendStatus === "INVALID_SESSION") {
    return true;
  }
  const detail = resolveRequestErrorMessage(error, "").toLowerCase();
  if (!detail) {
    return false;
  }
  return (
    detail.includes("invalid session") ||
    detail.includes("challenge expired") ||
    detail.includes("not allowed") ||
    detail.includes("not authorized") ||
    detail.includes("forbidden") ||
    detail.includes("auth_wallet_address already") ||
    detail.includes("metakeep wallet mismatch") ||
    detail.includes("metakeep wallet resolution failed")
  );
};

const resolveConnectDialogRef = (connectDialog) => {
  if (!connectDialog) return null;
  if (typeof connectDialog.show === "function") return connectDialog;
  if (
    connectDialog.value &&
    typeof connectDialog.value.show === "function"
  ) {
    return connectDialog.value;
  }
  return null;
};

export const logoutMetaKeepAndPromptLogin = async ({
  connectDialog = null,
  step = 2,
  onlySponsored = true,
} = {}) => {
  try {
    logDev("auth", "logout request", { url: getURLFromPieces(LOGOUT_ENDPOINT) });
    await axios.post(getURLFromPieces(LOGOUT_ENDPOINT));
    logDev("auth", "logout response ok");
  } catch (e) {
    logDev("auth", "logout failed", {
      status: e?.response?.status,
      message: e?.message,
    });
  }
  resetWalletSessionState({ clearStorage: true });

  const dialogRef = resolveConnectDialogRef(connectDialog);
  if (dialogRef) {
    try {
      await dialogRef.show(step, onlySponsored);
    } catch (error) {
      console.log(error);
    }
  }
};


export const getUserDateFormat = () => {
  const dateFormat = new Intl.DateTimeFormat().formatToParts(new Date());
  const formatOrder = dateFormat
    .filter((part) => ["day", "month", "year"].includes(part.type))
    .map((part) => part.type);

  return formatOrder.join("/");
};

export const parseDateFromLocal = (dateStr) => {
  const formatOrder = getUserDateFormat().split("/");
  const [datePart, timePart] = dateStr.split(",").map((str) => str.trim());
  const dateParts = datePart.split("/").map(Number);
  let day, month, year;
  formatOrder.forEach((format, index) => {
    if (format === "day") day = dateParts[index];
    if (format === "month") month = dateParts[index];
    if (format === "year") year = dateParts[index];
  });
  const parsedDate = new Date(year, month - 1, day);
  if (timePart) {
    const timeMatch = timePart.match(/(\d+):(\d+):(\d+)\s*(AM|PM)?/i);
    if (timeMatch) {
      let [_, hours, minutes, seconds, period] = timeMatch;
      hours = parseInt(hours, 10);
      minutes = parseInt(minutes, 10);
      seconds = parseInt(seconds, 10);
      if (period) {
        period = period.toUpperCase();
        if (period === "PM" && hours < 12) {
          hours += 12;
        } else if (period === "AM" && hours === 12) {
          hours = 0;
        }
      }
      parsedDate.setHours(hours, minutes, seconds);
    }
  }
  return parsedDate;
};

const padNumber = (value, size = 2) => {
  return String(Math.trunc(Math.abs(value))).padStart(size, "0");
};

export const parseTimestampOrDate = (value) => {
  if (!hasMeaningfulValue(value)) {
    return new Date(NaN);
  }
  if (value instanceof Date) {
    return new Date(value.getTime());
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const normalized = Math.abs(value) < 1e12 ? value * 1000 : value;
    return new Date(normalized);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return new Date(NaN);
    }
    if (/^-?\d+(?:\.\d+)?$/.test(trimmed)) {
      const numeric = Number(trimmed);
      if (Number.isFinite(numeric)) {
        const normalized = Math.abs(numeric) < 1e12 ? numeric * 1000 : numeric;
        return new Date(normalized);
      }
    }
    const parsed = new Date(trimmed);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
    try {
      const localized = parseDateFromLocal(trimmed);
      if (!Number.isNaN(localized.getTime())) {
        return localized;
      }
    } catch {
      // ignore parse errors and fall through to invalid date
    }
    return new Date(trimmed);
  }
  if (typeof value === "object") {
    if (typeof value.seconds === "number") {
      return parseTimestampOrDate(value.seconds);
    }
    if (typeof value.toNumber === "function") {
      try {
        return parseTimestampOrDate(value.toNumber());
      } catch {
        return new Date(NaN);
      }
    }
  }
  return new Date(NaN);
};

export const getStageRoi = (stagePrice, finalPrice) => {
  const normalizedStagePrice = Number(stagePrice ?? 0);
  const normalizedFinalPrice = Number(finalPrice ?? 0);
  if (normalizedStagePrice <= 0 || normalizedFinalPrice <= 0) {
    return 0;
  }
  const profit = Math.max(normalizedFinalPrice - normalizedStagePrice, 0);
  return Number(((profit / normalizedStagePrice) * 100).toFixed(2));
};

export const getDateByFormat = (value, format = "%Y-%m-%d") => {
  const date = parseTimestampOrDate(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const replacements = {
    "%Y": date.getFullYear().toString(),
    "%m": padNumber(date.getMonth() + 1),
    "%d": padNumber(date.getDate()),
    "%H": padNumber(date.getHours()),
    "%M": padNumber(date.getMinutes()),
    "%S": padNumber(date.getSeconds()),
  };
  return format.replace(/%[YmdHMS]/g, (token) => replacements[token] ?? token);
};

export const isPastDate = (dateStr) => {
  const parsedDate = parseDateFromLocal(dateStr);
  return parsedDate < new Date();
};

const checkAuthOnce = async () => {
  try {
    console.log("[CHECK-AUTH] calling", getURLFromPieces(CHECK_AUTH));
    const res = await axios.get(getURLFromPieces(CHECK_AUTH));
    console.log("[CHECK-AUTH] response", {
      status: res?.status,
      authenticated: Boolean(res?.data?.authenticated),
      wallet: res?.data?.wallet_address,
    });
    // A backend session may enrich an already-connected wallet, but it must
    // never replace or invent the active wallet identity.
    const sessionWallet = res?.data?.wallet_address;
    if (res?.data?.authenticated && sessionWallet) {
      const sessionRole = res.data.role;
      const hasSessionRole = sessionRole !== null && sessionRole !== undefined;
      const sessionExpiresAt = normalizeAuthSessionExpiry(
        res.data.expires_at
      );
      if (
        !hasSessionRole ||
        !Number.isInteger(Number(sessionRole)) ||
        sessionExpiresAt === null ||
        sessionExpiresAt <= Date.now()
      ) {
        await clearAuthenticatedWalletSession();
        return false;
      }
      const currentAccount = store.state.currentAccount ?? {};
      const storedMetaKeep = getStoredMetaKeepSession();
      const storedBusinessWallet = storedMetaKeep.businessWalletAddress || null;
      const storedAuthWallet = storedMetaKeep.authWalletAddress || null;
      const metakeepBusinessWallet =
        currentAccount.type === "MetaKeep"
          ? currentAccount.businessWalletAddress || storedBusinessWallet
          : storedBusinessWallet;
      const metakeepAuthWallet =
        currentAccount.type === "MetaKeep"
          ? currentAccount.authAddress || storedAuthWallet
          : storedAuthWallet;
      if (metakeepBusinessWallet || metakeepAuthWallet) {
        if (
          !ethers.utils.isAddress(metakeepBusinessWallet || "") ||
          !ethers.utils.isAddress(metakeepAuthWallet || "") ||
          !ethers.utils.isAddress(sessionWallet) ||
          metakeepAuthWallet.toLowerCase() !== sessionWallet.toLowerCase()
        ) {
          await clearAuthenticatedWalletSession();
          resetWalletSessionState({ clearStorage: true });
          return false;
        }
        const identity = await loadBackendSessionIdentity();
        if (
          !ethers.utils.isAddress(identity?.authWalletAddress || "") ||
          !ethers.utils.isAddress(identity?.businessWalletAddress || "") ||
          !Number.isInteger(identity?.role) ||
          identity.role !== Number(sessionRole) ||
          identity.authWalletAddress.toLowerCase() !==
            metakeepAuthWallet.toLowerCase() ||
          identity.businessWalletAddress.toLowerCase() !==
            metakeepBusinessWallet.toLowerCase()
        ) {
          await clearAuthenticatedWalletSession();
          resetWalletSessionState({ clearStorage: true });
          return false;
        }
        applyAuthSessionExpiry(sessionExpiresAt);
        setCarbonSession({
          businessWalletAddress: metakeepBusinessWallet,
          authWalletAddress: metakeepAuthWallet,
        });
        persistMetaKeepSession({
          businessWalletAddress: metakeepBusinessWallet,
          authWalletAddress: metakeepAuthWallet,
        });
        store.commit("setConnectedStatus", true);
        store.commit("setAccounts", [metakeepBusinessWallet]);
        store.commit("setCurrentAccount", {
          ...currentAccount,
          sdk: currentAccount.sdk || createMetaKeepSdk(),
          address: metakeepBusinessWallet,
          businessWalletAddress: metakeepBusinessWallet,
          authAddress: metakeepAuthWallet,
          type: "MetaKeep",
          role: Number(sessionRole),
          sessionRestored: !currentAccount.provider,
        });
        applyAdminRole(res.data.admin_role);
        logDev("auth", "checkAuth response", {
          authenticated: Boolean(res?.data?.authenticated),
          status: res?.status ?? null,
        });
        return res?.data?.authenticated;
      }
      applyAuthSessionExpiry(sessionExpiresAt);
      const currentAddr = currentAccount.address;
      const sameWallet =
        currentAddr &&
        currentAddr.toLowerCase() === String(sessionWallet).toLowerCase();
      const roleMismatch =
        hasSessionRole && !sessionMatchesRole(sessionRole);
      if (!sameWallet || roleMismatch) {
        console.log("[CHECK-AUTH] rejecting mismatched wallet session", {
          hasCurrentWallet: Boolean(currentAddr),
          roleMismatch,
        });
        await clearAuthenticatedWalletSession();
        return false;
      }
      if (currentAccount.type === "MetaMask") {
        try {
          const authority = await resolveInjectedWalletAuthority({
            provider: await getPreferredProvider(),
            currentAccountAddress: currentAddr,
            trackedAccounts: store.state.accounts,
          });
          requireMatchingWalletAddress(
            authority.address,
            sessionWallet,
            "Authenticated wallet session"
          );
        } catch (error) {
          logDev("auth", "checkAuth provider authority mismatch", {
            message: error?.message || String(error),
          });
          await clearAuthenticatedWalletSession();
          return false;
        }
      }
      let localDirectIdentity = null;
      if (
        isLocalTestnetMetaMaskEnabled() &&
        currentAccount.type !== "MetaKeep"
      ) {
        localDirectIdentity = await loadLocalDirectWalletSessionIdentity({
          expectedAuthWallet: sessionWallet,
          expectedRole: hasSessionRole ? Number(sessionRole) : null,
        });
      } else {
        setCarbonSession();
      }
      applyAdminRole(res.data.admin_role);
      store.commit("setAccounts", [currentAddr]);
      store.commit("setCurrentAccount", {
        ...currentAccount,
        ...localTestnetMetaMaskFields(currentAccount),
        address: currentAddr,
        authAddress: currentAccount.authAddress ?? currentAddr,
        ...(localDirectIdentity
          ? {
              businessWalletAddress:
                localDirectIdentity.businessWalletAddress,
            }
          : {}),
        ...(hasSessionRole ? { role: Number(sessionRole) } : {}),
      });
    }
    if (!res?.data?.authenticated) {
      invalidateAuthSession();
      setCarbonSession();
    }
    logDev("auth", "checkAuth response", {
      authenticated: Boolean(res?.data?.authenticated),
      status: res?.status ?? null,
    });
    return res?.data?.authenticated;
  } catch (error) {
    console.log("[CHECK-AUTH] error", {
      status: error?.response?.status,
      message: error?.message,
    });
    logDev("auth", "checkAuth error", {
      message: error?.message || String(error),
      status: error?.response?.status ?? null,
    });
    return false;
  }
};

export const checkAuth = async () => {
  if (authCheckInflight) {
    console.log("[CHECK-AUTH] in-flight reuse");
    return await authCheckInflight;
  }
  const inFlight = checkAuthOnce().finally(() => {
    if (authCheckInflight === inFlight) {
      authCheckInflight = null;
    }
  });
  authCheckInflight = inFlight;
  return await inFlight;
};

const normalizeTimeInput = (value) => {
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof value === "string") {
    if (!value.trim()) {
      return null;
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return null;
    }
    value = parsed;
  }
  if (typeof value !== "number" || Number.isNaN(value) || value < 0) {
    return null;
  }
  return value < 1e12 ? value * 1000 : value;
};

export const timeUntil = (time) => {
  const normalized = normalizeTimeInput(time);
  if (normalized === null) {
    return [0, 0];
  }
  const targetDate = new Date(normalized);
  if (!Number.isFinite(targetDate.getTime())) {
    return [0, 0];
  }
  let diff = targetDate.getTime() - Date.now();
  if (diff <= 0) {
    return [0, 0];
  }
  const hours = Math.floor(diff / (1000 * 60 * 60));
  diff -= hours * (1000 * 60 * 60);
  const minutes = Math.floor(diff / (1000 * 60));
  return [hours, minutes];
};

export const formatDate = (localDate, preLocale) => {
  let locale = "en-US";
  if (preLocale === "pt") {
    locale = "pt-BR";
  } else if (preLocale === "ja") {
    locale = "ja-JP";
  }
  const date = parseTimestampOrDate(localDate);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const presetLang = (locale) => {
  const supportedLocales = ["en", "ja", "pt"];
  const defaultLocale = "pt";
  const fromLocal = localStorage.getItem("selectedLanguage");
  if (supportedLocales.includes(fromLocal)) {
    locale.value = fromLocal;
    return;
  }
  locale.value = defaultLocale;

  return locale;
};

const unwrapBigNumbersInternal = (val, seen) => {
  if (BigNumber.isBigNumber(val)) {
    return val.toString();
  }
  if (val && val.type === "BigNumber" && typeof val.hex === "string") {
    return BigNumber.from(val.hex).toString();
  }
  if (typeof val === "number") {
    return val.toString();
  }
  if (Array.isArray(val)) {
    if (seen.has(val)) {
      throw new Error("Circular reference detected in unwrapBigNumbers");
    }
    seen.add(val);
    const result = val.map((item) => unwrapBigNumbersInternal(item, seen));
    seen.delete(val);
    return result;
  }
  if (val && typeof val === "object") {
    if (seen.has(val)) {
      throw new Error("Circular reference detected in unwrapBigNumbers");
    }
    seen.add(val);
    const out = {};
    for (const [k, v] of Object.entries(val)) {
      if (HOSTILE_KEYS.has(k)) {
        continue;
      }
      out[k] = unwrapBigNumbersInternal(v, seen);
    }
    seen.delete(val);
    return out;
  }
  return val;
};

export const unwrapBigNumbers = (val) => unwrapBigNumbersInternal(val, new WeakSet());

export const pollMetaKeepStatus = async (
  txHash,
  interval = 3000,
  maxAttempts = 20
) => {
  let attempts = 0;
  while (attempts < maxAttempts) {
    attempts += 1;
    const resp = await fetch(getURLFromPieces("metakeep", "tx", "status", txHash), {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!resp.ok) {
      throw new Error(`MetaKeep polling failed with status ${resp.status}`);
    }
    const statusPayload = await resp.json();
    const { status, failureReason, transactionHash } = statusPayload || {};
    console.log("MetaKeep tx status", statusPayload);

    if (status === "COMPLETED") {
      return transactionHash;
    }
    if (status === "FAILED") {
      throw new Error(`MetaKeep failed: ${failureReason}`);
    }
    if (!status) {
      throw new Error("MetaKeep status unavailable");
    }
    await new Promise((res) => setTimeout(res, interval));
  }
  throw new Error("MetaKeep status polling exceeded maximum attempts");
};

const unwrapInjectedProvider = (value) => {
  const candidate = value ? toRaw(value) : null;
  try {
    if (typeof candidate?.request === "function") {
      return candidate;
    }
    const nested = candidate?.provider ? toRaw(candidate.provider) : null;
    return typeof nested?.request === "function" ? nested : null;
  } catch {
    return null;
  }
};

export const getPreferredProvider = async () => {
  const walletInfo = toRaw(store.state.walletInfo || {});
  const currentAccount = toRaw(store.state.currentAccount || {});
  const walletId = walletInfo.walletId || currentAccount.walletId;
  const selectedWalletProvider = unwrapInjectedProvider(walletInfo.provider);
  if (selectedWalletProvider) {
    return selectedWalletProvider;
  }
  const currentAccountProvider = unwrapInjectedProvider(
    currentAccount.provider
  );
  if (walletId && currentAccountProvider) {
    return currentAccountProvider;
  }
  const wallets = detectWallets();
  if (walletId) {
    const selected = wallets.find((w) => w.id === walletId);
    if (selected) {
      return selected.provider;
    }
    throw new Error(`Selected wallet provider is unavailable: ${walletId}`);
  }
  const metamask = wallets.find((w) => w.id === "metamask");
  if (metamask) {
    return metamask.provider;
  }
  if (wallets.length > 0) {
    return wallets[0].provider;
  }
  if (typeof window !== "undefined" && window.ethereum) {
    return window.ethereum;
  }
  return null;
};


export function toFloatUnits(bi, decimals) {
  const bigValue = typeof bi === "bigint" ? bi : BigInt(bi);
  const d = Number(decimals);
  if (!Number.isInteger(d) || d < 0) {
    throw new TypeError("decimals must be a non-negative integer");
  }
  const s = bigValue.toString();
  if (d === 0) return Number(s);
  const pad = Math.max(0, d - s.length + 1);
  const str = (pad ? "0".repeat(pad) : "") + s;
  const split = str.length - d;
  const whole = split > 0 ? str.slice(0, split) : "0";
  const frac = str.slice(split);
  return Number(`${whole}.${frac}`);
}

export function projectForToken(args, rewardDecimals) {
  if (!args || typeof args !== "object") {
    throw new TypeError("projectForToken expects an args object");
  }
  const required = ["reserve", "userStake", "totalStake", "lastUpdate", "halfLife"];
  required.forEach((key) => {
    if (args[key] === undefined || args[key] === null) {
      throw new TypeError(`projectForToken missing required field ${key}`);
    }
  });

  const reserveF = toFloatUnits(BigInt(args.reserve), rewardDecimals);
  const userStakeTok = toFloatUnits(BigInt(args.userStake), 18);
  const totalStakeTok = toFloatUnits(BigInt(args.totalStake), 18);
  if (
    !Number.isFinite(totalStakeTok) ||
    !Number.isFinite(userStakeTok) ||
    totalStakeTok <= 0 ||
    userStakeTok <= 0
  ) {
    return { userEmitted: 0, userRatePerSec: 0 };
  }

  const safeReserve = Number.isFinite(reserveF) ? reserveF : Number.MAX_VALUE;
  const now = Math.floor(Date.now() / 1000);
  const numericLastUpdate = Number(args.lastUpdate);
  const numericUserLast = Number(args.userLastUpdate ?? args.lastUpdate ?? 0);
  const safeLast = Number.isFinite(numericLastUpdate) ? numericLastUpdate : 0;
  const safeUserLast = Number.isFinite(numericUserLast) ? numericUserLast : 0;
  const effectiveLastUpdate = Math.min(now, Math.max(safeLast, safeUserLast));
  const dt = Math.max(0, now - effectiveLastUpdate);

  const halfLife = Number(args.halfLife);
  if (!Number.isFinite(halfLife) || halfLife <= 0) {
    return { userEmitted: 0, userRatePerSec: 0 };
  }

  const LN2 = Math.log(2);
  const Rt = safeReserve * Math.pow(2, -dt / halfLife);
  const emitted = Math.max(0, safeReserve - Rt);

  const boostRaw = Number(args.userBoostWad ?? 0) / 1e18;
  const boost = Number.isFinite(boostRaw) && boostRaw >= 0 ? boostRaw : 0;
  const effW = userStakeTok * (1 + boost);
  const share = effW / Math.max(1e-18, totalStakeTok);

  const userEmitted = emitted * share;
  const rateNow = (LN2 / halfLife) * Rt;
  const userRatePerSec = rateNow * share;

  if (!Number.isFinite(userEmitted) || !Number.isFinite(userRatePerSec)) {
    return { userEmitted: 0, userRatePerSec: 0 };
  }

  return { userEmitted, userRatePerSec };
}

export function projectedSince(t0Sec, args, rewardDecimals) {
  const now = Math.floor(Date.now() / 1000);
  const tStart = Math.max(
    t0Sec,
    Number(args.lastUpdate),
    Number(args.userLastUpdate || 0)
  );
  if (tStart >= now) return 0;

  const reserveF = toFloatUnits(BigInt(args.reserve), rewardDecimals);
  const userStakeTok = toFloatUnits(BigInt(args.userStake), 18);
  const totalStakeTok = toFloatUnits(BigInt(args.totalStake), 18);
  if (totalStakeTok === 0 || userStakeTok === 0) return 0;

  const R_t0 =
    reserveF *
    Math.pow(2, -(tStart - Number(args.lastUpdate)) / Number(args.halfLife));
  const R_now = R_t0 * Math.pow(2, -(now - tStart) / Number(args.halfLife));
  const emitted = Math.max(0, R_t0 - R_now);

  const boost = Number(args.userBoostWad) / 1e18;
  const effW = userStakeTok * (1 + boost);
  const share = effW / Math.max(1e-18, totalStakeTok);

  return emitted * share;
}

export function startOfUtcDaySec() {
  const now = new Date();
  const utc =
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / 1000;
  return Math.floor(utc);
}

export function emittedOverDuration(
  reserveRaw,
  rewardDecimals,
  halfLifeSec,
  lastUpdateSec,
  durationSec
) {
  const reserveF = toFloatUnits(BigInt(reserveRaw), rewardDecimals);
  const now = Math.floor(Date.now() / 1000);
  const sinceLast = Math.max(0, now - Number(lastUpdateSec));
  const R_now = reserveF * Math.pow(2, -sinceLast / Number(halfLifeSec));
  const R_future =
    R_now * Math.pow(2, -Number(durationSec) / Number(halfLifeSec));
  return Math.max(0, R_now - R_future);
}

export function usdcPriceToFloat(priceRaw) {
  return toFloatUnits(BigInt(priceRaw), 6);
}

export function toOnChainYear(absYear, baseYear) {
  const i = Number(absYear) - Number(baseYear);
  return i + 1;
}
